import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, FileText, Clock, Zap, Loader2, AlertCircle, RotateCcw,
  Activity, Maximize, X, Copy, MessageSquare, ChevronDown, ChevronRight,
  Info, Play, Pause, Trash2, GitCompare, Trophy, Edit, Save, ChevronLeft,
  MessageCircle, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Star, Eye, Circle, BookOpen, ExternalLink, Upload, Scissors, Terminal
} from 'lucide-react';
import { openRouterService, TTMLFile, ChatMessage, ModelPersistence, TtmlJob, RefinementJob } from '../services/openRouterService';
import { useAuth } from '../services/AuthContext';
import { DeepgramResponse, SpeakerLabels } from '../types';
import { copyToClipboard } from '../utils/clipboard';
import { sessionService } from '../services/sessionService';
import { getLatestModels, listSessions as listAgentSessions, startTtmlGenerationSession, startTtmlRefinementSession, type AgentSession } from '../services/agentService';
import { useShareLink } from '../contexts/ShareLinkContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import ReactMarkdown from 'react-markdown';
import McpSetupModal from './McpSetupModal';

interface TTMLRankingEnhancedProps {
  transcript: string;
  filename: string;
  transcriptionData?: DeepgramResponse;
  speakerLabels?: SpeakerLabels;
  audioFile?: File;
  transcriptionChecksum?: string;
  sessionId?: string;
  currentSessionData?: any;
  onTtmlGenerated?: (ttmlFiles: Array<{ 
    name: string; 
    content: string; 
    filename?: string; 
    generatedAt?: Date; 
    checksum?: string;
    isValid?: boolean;
    validationErrors?: any[];
    validationWarnings?: any[];
    validationStats?: any;
  }>) => void;
  onTtmlPreview?: (ttmlId: string | null) => void;
  onTtmlSelected?: (ttmlId: string) => void;
}

interface EnhancedTTMLFile {
  id: string;
  name: string;
  content?: string;
  type: 'generated' | 'cached';
  source: 'standard' | 'best-of-3';
  model: string;
  generatedAt: Date;
  version?: number;  // Version number in the chain (1 = original, 2+ = refined)
  parentChecksum?: string;  // Track parent version for version history
  rootChecksum?: string;  // Original version in the chain
  childChecksums?: string[];  // Child versions (refined from this one)
  cumulativeCostUsd?: number | null;  // Cumulative cost across all versions
  tokenUsage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  checksum?: string;
  isSelected?: boolean;
  isValid?: boolean;
  validationErrors?: any[];
  validationWarnings?: any[];
  validationStats?: any;
  lastValidated?: string;
  generatedBy?: string | null;
  costUsd?: number | null;
  refinementLog?: any[];  // Agent SDK activity log for refinements
  revisionNotes?: string | null;  // Markdown notes from agent about what was changed
  editType?: 'manual' | 'agent' | null;  // Type of edit: manual edit or agent refinement
  editedBy?: string | null;  // Email of user who made manual edit
  noOpRefinements?: Array<{  // Track refinements that produced no changes
    iteration: number;
    model: string;
    costUsd: number;
    timestamp: string;
    message: string;
  }>;
  hasTimelineEdits?: boolean;  // Whether this revision has timeline edit cuts
  editCuts?: Array<{
    id: string;
    startTime: number;
    endTime: number;
    reason?: string;
    createdAt: string;
    createdBy: string;
  }>;
  isDraft?: boolean;
}

type AgentWorkflowKind = 'generation' | 'follow-up' | 'other';

const TTMLRankingEnhanced: React.FC<TTMLRankingEnhancedProps> = ({ 
  transcript, 
  filename, 
  transcriptionData, 
  speakerLabels, 
  audioFile, 
  transcriptionChecksum, 
  sessionId, 
  currentSessionData,
  onTtmlGenerated,
  onTtmlPreview,
  onTtmlSelected 
}) => {
  const { token } = useAuth();
  const { permissions: sharePermissions } = useShareLink();
  const { isDarkMode } = useDarkMode();
  const isPublishedOrYoutubeLinkedSession = useMemo(() => {
    const published = Boolean(
      currentSessionData?.published ||
      currentSessionData?.publishedToHomepage ||
      currentSessionData?.published_to_homepage
    );
    const youtubeUrl = currentSessionData?.youtubeUrl || currentSessionData?.youtube_url || currentSessionData?.videoUrl || '';
    const youtubeLinked = typeof youtubeUrl === 'string' && youtubeUrl.trim().length > 0;
    const youtubeMode = Boolean(currentSessionData?.isYoutubeMode || currentSessionData?.is_youtube_mode);
    return published || youtubeLinked || youtubeMode;
  }, [currentSessionData]);
  const [supportedModels, setSupportedModels] = useState<string[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [notification, setNotification] = useState<string>('');
  const [loadingModels, setLoadingModels] = useState(true);
  const [tokenUsage, setTokenUsage] = useState<{
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  } | null>(null);
  const [allTtmlFiles, setAllTtmlFiles] = useState<EnhancedTTMLFile[]>([]);
  const [loadingCachedVersions, setLoadingCachedVersions] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [selectedTtmlId, setSelectedTtmlId] = useState<string | null>(null);
  const selectedTtmlIdRef = React.useRef<string | null>(null); // Ref to avoid closure issues
  const userHasSelectedRef = React.useRef<boolean>(false); // Track if user has manually selected (prevents auto-selection overriding)
  // Server-side job tracking
  const [serverJobs, setServerJobs] = useState<TtmlJob[]>([]);
  const [refinementJobs, setRefinementJobs] = useState<RefinementJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  // Use ref for interval to avoid re-render cycles and prevent interval leaks
  const jobPollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  // Track whether we have active jobs to adjust polling frequency
  const hasActiveJobsRef = React.useRef(false);
  const processedCompletedJobIds = React.useRef<Set<string>>(new Set());
  const processedCompletedRefinementJobIds = React.useRef<Set<string>>(new Set());

  // Web access toggle for reference URL lookup during refinement
  const [enableWebAccess, setEnableWebAccess] = useState(false);
  const [isRefining, setIsRefining] = useState<string | null>(null); // Track which TTML is being refined

  // View TTML Modal state
  const [viewTtmlModal, setViewTtmlModal] = useState<{
    isOpen: boolean;
    file: EnhancedTTMLFile | null;
    content: string;
  }>({ isOpen: false, file: null, content: '' });

  // Enhanced TTML Editor state
  type TtmlEditorTab = 'raw' | 'topics' | 'edits' | 'clips' | 'references';
  const [editorTab, setEditorTab] = useState<TtmlEditorTab>('raw');
  const [editedContent, setEditedContent] = useState('');
  const [hasEdits, setHasEdits] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Parse TTML segments for editing
  interface TtmlSegment {
    id: string;
    begin: string;
    end: string;
    text: string;
    originalXml: string;
    // Structured fields for topics
    topicRank?: string; // e.g., "1" from "[1: 20/20]"
    topicPlacement?: string; // e.g., "20/20" or "20" from the rank
    topicTitle?: string; // e.g., "Intro Hook & Sponsor Messages"
    topicScore?: string; // e.g., "7/10 (S: 2/3, R: 1/2, C: 2/2, P: 1/2, CR: 1/1)"
    topicJustification?: string;
    topicOptimalPlacement?: string; // e.g., "20/20"
    topicContinuity?: string;
    // Structured fields for clips
    clipTitle?: string;
    clipType?: string; // "Viral Hook", "Educational Nugget", "Humorous Aside", "Cocktail Party Insight", "Constructive Framework"
    clipDescription?: string;
    clipVisual?: string;
    // Structured fields for edits
    editTitle?: string;
    editSuggestion?: string;
    editImprovement?: string;
    editPriority?: string; // "High", "Medium", "Low"
    // Structured fields for references
    refName?: string;
    refLinkText?: string;
    refUrl?: string;
    refCategory?: string;
    refContext?: string;
    refVisual?: string;
  }
  const [parsedSegments, setParsedSegments] = useState<{
    topics: TtmlSegment[];
    edits: TtmlSegment[];
    clips: TtmlSegment[];
    references: TtmlSegment[];
  }>({ topics: [], edits: [], clips: [], references: [] });

  // View Refinement Log Modal state
  const [viewLogModal, setViewLogModal] = useState<{
    isOpen: boolean;
    file: EnhancedTTMLFile | null;
  }>({ isOpen: false, file: null });

  const [showMcpModal, setShowMcpModal] = useState(false);

  // Track files currently being refined
  const [refiningFiles, setRefiningFiles] = useState<Set<string>>(new Set());

  // Refine Modal state
  const [refineModal, setRefineModal] = useState<{
    isOpen: boolean;
    file: EnhancedTTMLFile | null;
    instructions: string;
    iterationCount: number;
    blankSlate: boolean;
    maxIssuesToFix: number;  // 0 = unlimited
    topicDensity: number;  // Topics per hour: 5-30, default 18
  }>({ isOpen: false, file: null, instructions: '', iterationCount: 1, blankSlate: false, maxIssuesToFix: 0, topicDensity: 18 });

  // Generation modal state for validator-gated agent generation
  const [generateModal, setGenerateModal] = useState<{
    isOpen: boolean;
    topicDensity: number;  // Topics per hour: 5-30, default 18
    enableWebSearch: boolean;  // Enable agent web verification during generation
    includeEdits: boolean;  // Whether to create TTML edit suggestions
    additionalInstructions: string;  // Additional context/instructions for the prompt
  }>({ isOpen: false, topicDensity: 18, enableWebSearch: false, includeEdits: false, additionalInstructions: '' });

  // Upload TTML state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message: string;
    checksum?: string;
    isValid?: boolean;
    validationErrors?: any[];
    validationWarnings?: any[];
  } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Version selection for each TTML file (tracks which version is selected)
  const [selectedVersions, setSelectedVersions] = useState<Record<string, number>>({});

  // Track which version is being VIEWED per chain (rootChecksum -> checksum of viewed version)
  // This is separate from global TTML selection - it controls which version's metadata is shown in the row
  const [viewedVersions, setViewedVersions] = useState<Record<string, string>>({});

  // Refinement model selection — dynamically fetched from backend
  const [refinementModels, setRefinementModels] = useState<{ id: string; name: string }[]>([]);
  const [selectedRefinementModel, setSelectedRefinementModel] = useState<string>(() => {
    const persisted = ModelPersistence.getRefinementModel();
    return persisted || '';
  });

  // Fetch latest models on mount
  useEffect(() => {
    if (sharePermissions) {
      setLoadingModels(false);
      return;
    }

    getLatestModels()
      .then((data) => {
        if (data.models && data.models.length > 0) {
          const agentModels = data.models.map(m => ({ id: m.id, name: `${m.name} (${m.description})` }));
          const modelIds = data.models.map(m => m.id);
          setRefinementModels(agentModels);
          setSupportedModels(modelIds);

          const persistedGenerationModel = ModelPersistence.getSelectedModel();
          if (persistedGenerationModel && modelIds.includes(persistedGenerationModel)) {
            setSelectedModel(persistedGenerationModel);
          } else {
            const opusGenerationModel = data.models.find(m => m.tier === 'opus');
            setSelectedModel(opusGenerationModel?.id || modelIds[0]);
          }

          // Default refinement to Opus unless a persisted supported model exists.
          const opusModel = data.models.find(m => m.tier === 'opus');
          const persisted = ModelPersistence.getRefinementModel();
          if (persisted && data.models.some(m => m.id === persisted)) {
            setSelectedRefinementModel(persisted);
          } else {
            setSelectedRefinementModel(opusModel?.id || data.models[0].id);
          }
        }
      })
      .catch(() => { /* keep fallback models */ })
      .finally(() => setLoadingModels(false));
  }, [sharePermissions]);

  const [agentWorkflows, setAgentWorkflows] = useState<AgentSession[]>([]);
  const [loadingAgentWorkflows, setLoadingAgentWorkflows] = useState(false);
  const [trackedAgentSessionIds, setTrackedAgentSessionIds] = useState<string[]>([]);
  const [showRecentAgentGenerations, setShowRecentAgentGenerations] = useState(false);
  const [showDraftVersions, setShowDraftVersions] = useState(false);
  const trackedAgentSessionIdsRef = React.useRef<string[]>([]);
  const trackedAgentSessionKindsRef = React.useRef<Record<string, AgentWorkflowKind>>({});
  const MAX_CONCURRENT_GENERATIONS = 4;

  const trackAgentWorkflow = useCallback((agentSessionId: string, kind: AgentWorkflowKind = 'generation') => {
    trackedAgentSessionKindsRef.current[agentSessionId] = kind;
    if (trackedAgentSessionIdsRef.current.includes(agentSessionId)) return;
    const next = [...trackedAgentSessionIdsRef.current, agentSessionId];
    trackedAgentSessionIdsRef.current = next;
    setTrackedAgentSessionIds(next);
  }, []);

  const getAgentWorkflowKind = useCallback((session: AgentSession): AgentWorkflowKind => {
    const trackedKind = trackedAgentSessionKindsRef.current[session.id];
    if (trackedKind) return trackedKind;

    const title = (session.title || '').toLowerCase();
    if (title.includes('refine') || title.includes('follow-up') || title.includes('timeline edit')) {
      return 'follow-up';
    }
    if (title.includes('ttml') || title.includes('caption') || title.includes('timeline')) {
      return 'generation';
    }
    return 'other';
  }, []);

  const isTtmlAgentWorkflow = useCallback((session: AgentSession) => {
    if (trackedAgentSessionIdsRef.current.includes(session.id)) return true;
    const title = (session.title || '').toLowerCase();
    return title.includes('ttml') || title.includes('caption') || title.includes('timeline');
  }, []);

  const loadAgentWorkflows = useCallback(async () => {
    if (!sessionId || sharePermissions) {
      setAgentWorkflows([]);
      return;
    }

    setLoadingAgentWorkflows(true);
    try {
      const sessions = await listAgentSessions(sessionId, token || undefined);
      const relevant = sessions
        .filter(isTtmlAgentWorkflow)
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, 12);
      setAgentWorkflows(relevant);
    } catch (err) {
      console.error('[TTML Agent] Failed to load agent workflows:', err);
    } finally {
      setLoadingAgentWorkflows(false);
    }
  }, [isTtmlAgentWorkflow, sessionId, sharePermissions]);

  const activeAgentWorkflowCount = useMemo(
    () => agentWorkflows.filter(workflow => workflow.status === 'active').length,
    [agentWorkflows]
  );

  const recentAgentGenerationSessions = useMemo(
    () => agentWorkflows.filter(workflow => getAgentWorkflowKind(workflow) === 'generation').slice(0, 4),
    [agentWorkflows, getAgentWorkflowKind]
  );

  const activeAgentGenerationCount = useMemo(
    () => recentAgentGenerationSessions.filter(workflow => workflow.status === 'active').length,
    [recentAgentGenerationSessions]
  );

  const draftVersionCount = useMemo(
    () => allTtmlFiles.filter(file => file.isDraft).length,
    [allTtmlFiles]
  );

  const visibleTtmlFiles = useMemo(
    () => showDraftVersions ? allTtmlFiles : allTtmlFiles.filter(file => !file.isDraft),
    [allTtmlFiles, showDraftVersions]
  );

  useEffect(() => {
    if (activeAgentGenerationCount > 0) {
      setShowRecentAgentGenerations(true);
    }
  }, [activeAgentGenerationCount]);

  useEffect(() => {
    setShowRecentAgentGenerations(false);
    setShowDraftVersions(false);
  }, [sessionId]);

  useEffect(() => {
    loadAgentWorkflows();
    if (!sessionId || sharePermissions) return;

    const interval = setInterval(() => {
      loadAgentWorkflows();
    }, 10000);

    return () => clearInterval(interval);
  }, [loadAgentWorkflows, sessionId, sharePermissions, trackedAgentSessionIds]);

  // Persist selected model to localStorage
  useEffect(() => {
    if (selectedModel) {
      ModelPersistence.setSelectedModel(selectedModel);
    }
  }, [selectedModel]);

  // Persist refinement model to localStorage
  useEffect(() => {
    if (selectedRefinementModel) {
      ModelPersistence.setRefinementModel(selectedRefinementModel);
    }
  }, [selectedRefinementModel]);

  // Load and poll server jobs (including refinement jobs)
  useEffect(() => {
    if (!sessionId || !token || sharePermissions) return;

    const loadServerJobs = async () => {
      try {
        const response = await openRouterService.getTtmlJobs(sessionId, token);
        if (response.success) {
          setServerJobs(response.jobs);
        }
      } catch (err) {
        console.error('[TTML Jobs] Failed to load server jobs:', err);
      }
    };

    const loadRefineJobs = async () => {
      try {
        const response = await openRouterService.getRefinementJobs(sessionId, token);
        if (response.success) {
          setRefinementJobs(response.jobs);

          // Check for newly completed jobs and refresh files
          for (const job of response.jobs) {
            if ((job.status === 'completed' || job.status === 'no_changes' || job.status === 'failed') &&
                !processedCompletedRefinementJobIds.current.has(job.job_id)) {
              processedCompletedRefinementJobIds.current.add(job.job_id);
              console.log(`[REFINE-JOB] Job ${job.job_id} finished (${job.status}) - refreshing files`);

              // Remove from refining set
              setRefiningFiles(prev => {
                const next = new Set(prev);
                next.delete(job.source_checksum);
                return next;
              });

              if (job.status === 'completed' || job.status === 'no_changes') {
                // Parse results
                const resultChecksums = job.result_checksums ? JSON.parse(job.result_checksums) : [];
                const noChangeIterations = job.no_change_iterations ? JSON.parse(job.no_change_iterations) : [];
                const costStr = job.total_cost_usd ? ` ($${job.total_cost_usd.toFixed(2)})` : '';

                if (resultChecksums.length > 0) {
                  setNotification(`Refinement complete! ${resultChecksums.length} new version(s) created${noChangeIterations.length > 0 ? `, ${noChangeIterations.length} no-op(s)` : ''}${costStr}`);
                } else if (noChangeIterations.length > 0) {
                  setNotification(`Refinement complete - no changes detected in ${noChangeIterations.length} iteration(s)${costStr}`);
                }

                // Refresh TTML files to show new versions
                console.log(`[REFINE-JOB] Triggering TTML refresh after job ${job.job_id} completed`)
                loadAllTtmlFiles();
                setTimeout(() => setNotification(''), 5000);
              } else if (job.status === 'failed') {
                setError(`Refinement failed: ${job.error_message || 'Unknown error'}`);
              }
            }
          }

          // Update refining files based on active jobs
          const activeJobSourceChecksums = response.jobs
            .filter(j => j.status === 'pending' || j.status === 'refining')
            .map(j => j.source_checksum);

          setRefiningFiles(new Set(activeJobSourceChecksums));
        }
      } catch (err) {
        console.error('[Refinement Jobs] Failed to load refinement jobs:', err);
      }
    };

    // Initial load
    loadServerJobs();
    loadRefineJobs();

    // Set up adaptive polling - starts fast (2s), slows down (10s) when no active jobs
    // Use a single interval with adaptive delay based on hasActiveJobsRef
    const FAST_POLL_MS = 2000;
    const SLOW_POLL_MS = 10000;
    let lastPollTime = Date.now();

    const pollJobs = () => {
      const now = Date.now();
      const delay = hasActiveJobsRef.current ? FAST_POLL_MS : SLOW_POLL_MS;

      if (now - lastPollTime >= delay) {
        lastPollTime = now;
        loadServerJobs();
        loadRefineJobs();
      }
    };

    // Use a single fast interval that internally checks if it should poll
    const interval = setInterval(pollJobs, FAST_POLL_MS);
    jobPollingIntervalRef.current = interval;

    return () => {
      if (jobPollingIntervalRef.current) {
        clearInterval(jobPollingIntervalRef.current);
        jobPollingIntervalRef.current = null;
      }
    };
  }, [sessionId, token, sharePermissions]);

  // Detect newly completed jobs whenever serverJobs changes
  useEffect(() => {
    if (!sessionId || !token) return;

    const completedJobs = serverJobs.filter(j => j.status === 'completed');
    const newlyCompletedJobs = completedJobs.filter(j => !processedCompletedJobIds.current.has(j.job_id));

    if (newlyCompletedJobs.length > 0) {
      console.log('[TTML Jobs] New completed jobs detected:', newlyCompletedJobs.map(j => j.job_id));
      console.log('[TTML Jobs] Current allTtmlFiles count BEFORE refresh:', allTtmlFiles.length);
      // Mark these jobs as processed
      newlyCompletedJobs.forEach(j => processedCompletedJobIds.current.add(j.job_id));
      // Refresh the TTML file list
      console.log('[TTML Jobs] Calling loadAllTtmlFiles() to refresh...');
      loadAllTtmlFiles().then(() => {
        console.log('[TTML Jobs] loadAllTtmlFiles() completed');
      });
    }
  }, [serverJobs, sessionId, token]);

  // Update hasActiveJobsRef when job status changes (for adaptive polling)
  useEffect(() => {
    const activeGenerationJobs = serverJobs.filter(j => j.status === 'pending' || j.status === 'generating' || j.status === 'refining');
    const activeRefineJobs = refinementJobs.filter(j => j.status === 'pending' || j.status === 'refining');
    hasActiveJobsRef.current = activeGenerationJobs.length > 0 || activeRefineJobs.length > 0;
  }, [serverJobs, refinementJobs]);

  // Keep ref in sync with state for use in async functions
  useEffect(() => {
    selectedTtmlIdRef.current = selectedTtmlId;
  }, [selectedTtmlId]);

  // Initialize from parent's session data if available
  useEffect(() => {
    if (currentSessionData?.generatedTtmlFiles && Array.isArray(currentSessionData.generatedTtmlFiles) && currentSessionData.generatedTtmlFiles.length > 0 && allTtmlFiles.length === 0) {
      console.log('[TTMLRanking] Initializing from parent session data:', currentSessionData.generatedTtmlFiles.length, 'files');

      const initialFiles: EnhancedTTMLFile[] = currentSessionData.generatedTtmlFiles.map((file: any) => ({
        id: file.checksum || `${file.name || file.description || 'unknown'}-${Date.now()}`,
        name: file.name || file.description || file.filename || `Version ${file.version || '?'}`,
        content: file.content,
        type: 'cached' as const,
        source: 'standard' as const,
        model: file.model || 'unknown',
        generatedAt: file.generatedAt,
        version: file.version || 1,
        parentChecksum: file.parentChecksum,
        rootChecksum: file.rootChecksum || file.checksum, // Fall back to own checksum if no root
        childChecksums: file.childChecksums || [],
        tokenUsage: null,
        checksum: file.checksum,
        isValid: file.isValid,
        validationErrors: file.validationErrors,
        validationWarnings: file.validationWarnings,
        validationStats: file.validationStats,
        lastValidated: file.lastValidated,
        generatedBy: file.generatedBy,
        costUsd: file.costUsd,
        cumulativeCostUsd: file.cumulativeCostUsd,
        refinementLog: file.refinementLog, // Add refinement log for viewing agent activity
        revisionNotes: file.revisionNotes, // Manual edit revision notes
        editType: file.editType, // 'manual' or 'agent'
        editedBy: file.editedBy, // User who made manual edit
        noOpRefinements: file.noOpRefinements, // Track no-op refinements
        isDraft: Boolean((file as any).isDraft),
        isSelected: currentSessionData.selectedTtmlId === file.checksum
      }));
      
      setAllTtmlFiles(initialFiles);

      // Set selected TTML if available, but don't override user's manual selection
      if (currentSessionData.selectedTtmlId && !userHasSelectedRef.current) {
        console.log('[TTML-SELECT] Setting selectedTtmlId from currentSessionData (initial load):', currentSessionData.selectedTtmlId);
        setSelectedTtmlId(currentSessionData.selectedTtmlId);
        selectedTtmlIdRef.current = currentSessionData.selectedTtmlId;
      } else if (userHasSelectedRef.current) {
        console.log('[TTML-SELECT] Skipping currentSessionData selection - user has already made a selection');
      }
    }
  }, [currentSessionData]);

  // Handle new files added to currentSessionData (e.g., from manual reference edits)
  // Use a ref to track files we've already validated to avoid duplicate validation calls
  const validatedNewFilesRef = React.useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!currentSessionData?.generatedTtmlFiles || allTtmlFiles.length === 0) return;

    // Check if there are new files in currentSessionData that we don't have
    const existingChecksums = new Set(allTtmlFiles.map(f => f.checksum));
    const newFiles = currentSessionData.generatedTtmlFiles.filter(
      (file: any) => file.checksum && !existingChecksums.has(file.checksum)
    );

    if (newFiles.length > 0) {
      console.log('[TTMLRanking] Found', newFiles.length, 'new files from parent, adding them');

      const mappedNewFiles: EnhancedTTMLFile[] = newFiles.map((file: any) => ({
        id: file.checksum || `${file.name || file.description || 'unknown'}-${Date.now()}`,
        name: file.name || file.description || file.filename || `Version ${file.version || '?'}`,
        content: file.content,
        type: 'cached' as const,
        source: 'standard' as const,
        model: file.model || 'unknown',
        generatedAt: file.generatedAt,
        version: file.version || 1,
        parentChecksum: file.parentChecksum,
        rootChecksum: file.rootChecksum || file.checksum,
        childChecksums: file.childChecksums || [],
        tokenUsage: null,
        checksum: file.checksum,
        isValid: file.isValid,
        validationErrors: file.validationErrors,
        validationWarnings: file.validationWarnings,
        validationStats: file.validationStats,
        lastValidated: file.lastValidated,
        generatedBy: file.generatedBy,
        costUsd: file.costUsd,
        cumulativeCostUsd: file.cumulativeCostUsd,
        refinementLog: file.refinementLog,
        revisionNotes: file.revisionNotes,
        editType: file.editType,
        editedBy: file.editedBy,
        noOpRefinements: file.noOpRefinements,
        isDraft: Boolean((file as any).isDraft),
        isSelected: file.isSelected || currentSessionData.selectedTtmlId === file.checksum
      }));

      // Add new files and update selection
      setAllTtmlFiles(prev => {
        // Update isSelected on all existing files first
        const updatedExisting = prev.map(f => ({
          ...f,
          isSelected: currentSessionData.selectedTtmlId === f.checksum
        }));
        return [...updatedExisting, ...mappedNewFiles];
      });

      // Update selected TTML ID if the new file is selected
      if (currentSessionData.selectedTtmlId) {
        const newSelectedFile = mappedNewFiles.find(f => f.isSelected);
        if (newSelectedFile) {
          console.log('[TTML-SELECT] New file is selected, updating selectedTtmlId:', newSelectedFile.checksum?.substring(0, 8));
          setSelectedTtmlId(currentSessionData.selectedTtmlId);
          selectedTtmlIdRef.current = currentSessionData.selectedTtmlId;
        }
      }

      // Validate newly added files after a short delay to let state settle
      // Only validate files we haven't already validated
      setTimeout(() => {
        mappedNewFiles.forEach(file => {
          if (file.content && file.checksum && !validatedNewFilesRef.current.has(file.checksum)) {
            validatedNewFilesRef.current.add(file.checksum);
            console.log('[TTMLRanking] Auto-validating new file:', file.name);
            validateSingleTtml(file);
          }
        });
      }, 500);
    }
  }, [currentSessionData?.generatedTtmlFiles, currentSessionData?.selectedTtmlId, allTtmlFiles.length]);

  // Load cached versions and session data when component mounts or transcript changes
  // Use a ref to prevent multiple loads
  const hasLoadedCache = React.useRef(false);
  // Ref to prevent double-click save race conditions
  const isSavingRef = React.useRef(false);
  useEffect(() => {
    // Always load from cache to get the latest TTML files
    // The cache may have newer files than the session's generatedTtmlFiles
    if (transcript && sessionId && token && !hasLoadedCache.current) {
      hasLoadedCache.current = true;
      console.log('[TTMLRanking] Loading cached TTML files from backend');
      loadAllTtmlFiles().then(() => {
        // Auto-validate after loading to ensure stats are available
        setTimeout(() => {
          validateAllTtmlFiles();
        }, 1000);
      });
      loadSessionSelectedTtml();
    }
  }, [transcript, token, sessionId]);

  useEffect(() => {
    const handleAgentTtmlUpdated = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (!sessionId || detail.sessionId !== sessionId) return;

      if (detail.selectedTtmlId) {
        setSelectedTtmlId(detail.selectedTtmlId);
        selectedTtmlIdRef.current = detail.selectedTtmlId;
      }

      setRefiningFiles(new Set());
      loadAllTtmlFiles();
      loadAgentWorkflows();
    };

    window.addEventListener('agent-ttml-updated', handleAgentTtmlUpdated);
    return () => window.removeEventListener('agent-ttml-updated', handleAgentTtmlUpdated);
  }, [loadAgentWorkflows, sessionId, transcript, token, selectedModel]);

  useEffect(() => {
    const handleNavigateToAgent = () => {
      loadAgentWorkflows();
    };

    window.addEventListener('navigate-to-agent', handleNavigateToAgent);
    return () => window.removeEventListener('navigate-to-agent', handleNavigateToAgent);
  }, [loadAgentWorkflows]);

  const loadSessionSelectedTtml = async () => {
    if (!sessionId || !token) return;

    // Don't override user's manual selection
    if (userHasSelectedRef.current) {
      console.log('[TTML-SELECT] Skipping loadSessionSelectedTtml - user has already made a selection');
      return;
    }

    try {
      const session = await sessionService.getSession(sessionId, token);
      if (session.selectedTtmlId) {
        console.log('[TTML-SELECT] Setting selectedTtmlId from server session fetch:', session.selectedTtmlId);
        setSelectedTtmlId(session.selectedTtmlId);
        selectedTtmlIdRef.current = session.selectedTtmlId;
      }
    } catch (err) {
      console.error('Failed to load session selected TTML:', err);
    }
  };

  const loadAllTtmlFiles = async () => {
    if (!transcript || !token) return;

    setLoadingCachedVersions(true);
    try {
      const response = await openRouterService.getCachedTTMLVersions({
        transcript,
        model: selectedModel || 'all', // Pass model but backend ignores it now
        sessionId: sessionId
      }, token);

      if (response.success) {
        console.log('[TTMLRanking] Loaded cached TTML versions:', response.cachedVersions.length);
        console.log('[TTMLRanking] Cached versions details:', response.cachedVersions.map((v: any) => ({
          filename: v.filename,
          checksum: v.checksum?.substring(0, 8),
          version: v.version,
          rootChecksum: v.rootChecksum?.substring(0, 8),
          parentChecksum: v.parentChecksum?.substring(0, 8),
          isValid: v.isValid,
          hasStats: !!v.validationStats
        })));
        
        // Convert cached versions to our unified format
        const cachedFiles = response.cachedVersions.map((version: any, index: number) => {
          const source = version.type === 'best-of-3' ? 'best-of-3' : 'standard';
          return {
            id: `cached_${version.checksum}_${index}_${Date.now()}`, // Unique ID even for cached files
            name: version.filename,
            type: 'cached' as const,
            source: source as 'best-of-3' | 'standard',
            model: version.model,
            generatedAt: new Date(version.generatedAt),
            version: version.version || 1,
            parentChecksum: version.parentChecksum,
            rootChecksum: version.rootChecksum || version.checksum,
            childChecksums: version.childChecksums || [],
            cumulativeCostUsd: version.cumulativeCostUsd || version.costUsd,
            tokenUsage: version.tokenUsage,
            checksum: version.checksum,
            isSelected: selectedTtmlIdRef.current === version.checksum,
            isValid: version.isValid,
            validationErrors: version.validationErrors,
            validationWarnings: version.validationWarnings,
            validationStats: version.validationStats,
            lastValidated: version.lastValidated,
            generatedBy: version.generatedBy,
            costUsd: version.costUsd,
            refinementLog: version.refinementLog, // Add refinement log for viewing agent activity
            revisionNotes: version.revisionNotes, // Add revision notes from agent
            editType: version.editType, // 'manual' or 'agent'
            editedBy: version.editedBy, // User who made manual edit
            hasTimelineEdits: version.hasTimelineEdits || false, // Timeline edit cuts from refinement
            editCuts: version.editCuts || null, // Edit cut details
            isDraft: Boolean((version as any).isDraft)
          };
        });
        
        console.log('[TTMLRanking] Files with validation stats:',
          cachedFiles.filter(f => f.validationStats).length,
          'out of', cachedFiles.length, 'total files');

        // Log rootChecksum grouping info for debugging
        const rootChecksumGroups = new Map<string, string[]>();
        for (const f of cachedFiles) {
          const rootKey = f.rootChecksum || f.checksum;
          if (!rootChecksumGroups.has(rootKey)) {
            rootChecksumGroups.set(rootKey, []);
          }
          rootChecksumGroups.get(rootKey)!.push(f.checksum?.substring(0, 8) + '(v' + f.version + ')');
        }
        console.log('[TTMLRanking] Version chains from cache:', Object.fromEntries(rootChecksumGroups));

        // Use functional update to get current state (avoid stale closure)
        console.log('[TTMLRanking] About to merge. Server returned', cachedFiles.length, 'files with checksums:',
          cachedFiles.map(f => f.checksum?.substring(0, 8)));

        setAllTtmlFiles(prevFiles => {
          console.log('[TTMLRanking] Inside setState - prevFiles count:', prevFiles.length,
            'checksums:', prevFiles.map(f => f.checksum?.substring(0, 8)));

          // Only replace if we don't have any files yet, otherwise merge
          if (prevFiles.length === 0) {
            console.log('[TTMLRanking] Setting cached files (initial load):', cachedFiles.length);
            // Always update parent with cache files to sync session state
            if (cachedFiles.length > 0) {
              setTimeout(() => updateParentWithAllTtmlFiles(cachedFiles), 0);
            }
            return cachedFiles;
          } else {
            // Merge cached files with existing files - update existing with cache data (has more complete info like refinementLog)
            const cachedByChecksum = new Map(cachedFiles.map(f => [f.checksum || f.id, f]));
            const existingChecksums = new Set(prevFiles.map(f => f.checksum || f.id));

            // Update existing files with cache data (which has refinementLog and other fields)
            const updatedFiles = prevFiles.map(existing => {
              const cached = cachedByChecksum.get(existing.checksum || existing.id);
              if (cached) {
                // Merge cache data into existing, preserving existing values unless cache has more info
                return {
                  ...existing,
                  refinementLog: cached.refinementLog || existing.refinementLog,
                  revisionNotes: cached.revisionNotes || existing.revisionNotes,
                  editType: cached.editType || existing.editType,
                  editedBy: cached.editedBy || existing.editedBy,
                  version: cached.version || existing.version,
                  parentChecksum: cached.parentChecksum || existing.parentChecksum,
                  rootChecksum: cached.rootChecksum || existing.rootChecksum,
                  childChecksums: cached.childChecksums || existing.childChecksums,
                  cumulativeCostUsd: cached.cumulativeCostUsd || existing.cumulativeCostUsd,
                  generatedBy: cached.generatedBy || existing.generatedBy,
                  // Also update validation data from cache
                  isValid: cached.isValid !== undefined ? cached.isValid : existing.isValid,
                  validationStats: cached.validationStats || existing.validationStats,
                  validationErrors: cached.validationErrors || existing.validationErrors,
                  validationWarnings: cached.validationWarnings || existing.validationWarnings,
                  // Timeline edit cuts
                  hasTimelineEdits: cached.hasTimelineEdits || existing.hasTimelineEdits || false,
                  editCuts: cached.editCuts || existing.editCuts || null
                };
              }
              return existing;
            });

            // Find truly new files not in existing
            const newCachedFiles = cachedFiles.filter(f => !existingChecksums.has(f.checksum || f.id));
            console.log('[TTMLRanking] Merge result: existingChecksums:', Array.from(existingChecksums).map((c: unknown) => (c as string)?.substring(0, 8)),
              'newCachedFiles count:', newCachedFiles.length,
              'new checksums:', newCachedFiles.map(f => f.checksum?.substring(0, 8)));

            const finalFiles = newCachedFiles.length > 0 ? [...updatedFiles, ...newCachedFiles] : updatedFiles;

            if (newCachedFiles.length > 0) {
              console.log('[TTMLRanking] Found', newCachedFiles.length, 'new cached files to add, existing:', prevFiles.length);
              // Trigger parent update outside of setState
              setTimeout(() => updateParentWithAllTtmlFiles(newCachedFiles), 0);
            } else {
              console.log('[TTMLRanking] Updated existing files with cache data');
            }

            return finalFiles;
          }
        });
        
        // Auto-select first TTML if none selected AND no session has a selection
        // This should only happen on truly fresh sessions with no prior selection
        // Use ref to get current value (not stale closure value)
        // Also skip if user has already made a manual selection
        const currentSelection = selectedTtmlIdRef.current;
        if (!currentSelection && !userHasSelectedRef.current && cachedFiles.length > 0 && sessionId) {
          console.log('[TTML-SELECT] Auto-selecting first TTML (no prior selection):', cachedFiles[0].checksum?.substring(0, 8));
          await selectTtml(cachedFiles[0].checksum!, false); // false = not user initiated
        } else {
          console.log('[TTML-SELECT] Skipping auto-select - currentSelection:', currentSelection?.substring(0, 8), 'userHasSelected:', userHasSelectedRef.current, 'cachedFiles:', cachedFiles.length);
        }
      }
    } catch (err) {
      console.error('Failed to load cached versions:', err);
    } finally {
      setLoadingCachedVersions(false);
    }
  };

  const updateParentWithAllTtmlFiles = async (files: EnhancedTTMLFile[]) => {
    console.log('[TTML-PARENT-UPDATE] Starting update with', files.length, 'files');
    if (!onTtmlGenerated) {
      console.warn('[TTML-PARENT-UPDATE] No onTtmlGenerated callback provided');
      return;
    }
    
    try {
      const allTtmlFiles = [];
      
      for (const file of files) {
        console.log('[TTML-PARENT-UPDATE] Processing file:', {
          name: file.name,
          type: file.type,
          hasContent: !!file.content,
          checksum: file.checksum,
          isValid: file.isValid,
          hasStats: !!file.validationStats
        });
        
        let content: string;
        if (file.type === 'cached' && !file.content) {
          try {
            const blob = await openRouterService.downloadCachedTTML(file.checksum!, token!);
            content = await blob.text();
            console.log('[TTML-PARENT-UPDATE] Downloaded cached content for', file.name, 'length:', content.length);
          } catch (err) {
            console.warn(`Failed to fetch cached content for ${file.name}:`, err);
            continue;
          }
        } else {
          content = file.content!;
        }
        
        const ttmlFile = {
          name: file.name,
          content,
          filename: file.name,
          generatedAt: file.generatedAt,
          checksum: file.checksum,
          isValid: file.isValid,
          validationErrors: file.validationErrors,
          validationWarnings: file.validationWarnings,
          validationStats: file.validationStats,
          // Version tracking fields
          version: file.version,
          parentChecksum: file.parentChecksum,
          rootChecksum: file.rootChecksum,
          childChecksums: file.childChecksums,
          // Cost and metadata
          model: file.model,
          costUsd: file.costUsd,
          cumulativeCostUsd: file.cumulativeCostUsd,
          generatedBy: file.generatedBy,
          // Refinement log for viewing agent activity
          refinementLog: file.refinementLog,
          noOpRefinements: file.noOpRefinements
        };
        
        console.log('[TTML-PARENT-UPDATE] Prepared TTML file:', {
          name: ttmlFile.name,
          checksum: ttmlFile.checksum,
          contentLength: ttmlFile.content?.length,
          stats: ttmlFile.validationStats
        });
        
        allTtmlFiles.push(ttmlFile);
      }
      
      console.log('[TTML-PARENT-UPDATE] Calling onTtmlGenerated with', allTtmlFiles.length, 'files');
      if (allTtmlFiles.length > 0) {
        onTtmlGenerated(allTtmlFiles);
      }
    } catch (err) {
      console.error('Failed to update parent with all TTML files:', err);
    }
  };

  const selectTtml = async (ttmlId: string, isUserInitiated: boolean = true) => {
    if (!sessionId || !token) return;

    console.log('[TTML-SELECT] selectTtml called with:', ttmlId?.substring(0, 8), 'userInitiated:', isUserInitiated);

    // Mark that user has made a selection (prevents auto-selection from overriding)
    if (isUserInitiated) {
      userHasSelectedRef.current = true;
      console.log('[TTML-SELECT] User has manually selected, disabling auto-selection override');
    }

    try {
      setSelectedTtmlId(ttmlId);
      console.log('[TTML-SELECT] Local state updated to:', ttmlId?.substring(0, 8));

      // Update session with selected TTML
      await sessionService.updateSession({
        sessionId: sessionId,
        selectedTtmlId: ttmlId
      }, token);

      // Update local state
      setAllTtmlFiles(prev => prev.map(file => ({
        ...file,
        isSelected: file.checksum === ttmlId || file.id === ttmlId
      })));

      // Call onTtmlSelected to update selection in parent component
      if (onTtmlSelected) {
        onTtmlSelected(ttmlId);
      }

      setNotification('Metadata revision selection saved');
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      console.error('Failed to save TTML selection:', err);
      setError('Failed to save TTML selection');
    }
  };

  const validateSingleTtml = async (ttmlFile: EnhancedTTMLFile) => {
    if (!ttmlFile.content || !ttmlFile.checksum) return;
    
    try {
      console.log('[TTML-VALIDATE] Validating single TTML:', ttmlFile.name);
      
      // Import the validation function from the backend
      const { comprehensiveTTMLValidation } = await import('../utils/ttmlValidation');
      
      // Run validation with episode duration for accurate coverage checks
      const sessionDuration = currentSessionData?.transcriptionData?.metadata?.duration ||
                             currentSessionData?.duration || 0;
      const validationResult = comprehensiveTTMLValidation(ttmlFile.content, {
        expectedDuration: sessionDuration > 0 ? sessionDuration : null,
      });
      
      console.log('[TTML-VALIDATE] Validation result for', ttmlFile.name, ':', {
        isValid: validationResult.isValid,
        errors: validationResult.errors.length,
        warnings: validationResult.warnings.length,
        stats: validationResult.stats
      });
      
      // Update the file with validation results
      setAllTtmlFiles(prev => prev.map(file => {
        if (file.id === ttmlFile.id || file.checksum === ttmlFile.checksum) {
          return {
            ...file,
            isValid: validationResult.isValid,
            validationErrors: validationResult.errors,
            validationWarnings: validationResult.warnings,
            validationStats: validationResult.stats,
            lastValidated: new Date().toISOString()
          };
        }
        return file;
      }));
      
      // Also update the parent with the validated file using the latest state
      setAllTtmlFiles(prev => {
        const updatedFiles = prev.map(file => {
          if (file.id === ttmlFile.id || file.checksum === ttmlFile.checksum) {
            return {
              ...file,
              isValid: validationResult.isValid,
              validationErrors: validationResult.errors,
              validationWarnings: validationResult.warnings,
              validationStats: validationResult.stats,
              lastValidated: new Date().toISOString()
            };
          }
          return file;
        });
        
        // Update parent with the validated files
        updateParentWithAllTtmlFiles(updatedFiles);
        
        return updatedFiles; // Return the updated files
      });
      
      // If validation is available from backend, also call backend validation for persistence
      if (sessionId && token && ttmlFile.checksum) {
        try {
          const response = await fetch(`${process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:7777/api'}/sessions/${sessionId}/validate-ttml`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              checksums: [ttmlFile.checksum]
            })
          });
          
          if (response.ok) {
            console.log('[TTML-VALIDATE] Backend validation completed for', ttmlFile.name);
          }
        } catch (err) {
          console.warn('[TTML-VALIDATE] Backend validation failed, but client-side validation succeeded');
        }
      }
      
    } catch (err) {
      console.error('[TTML-VALIDATE] Failed to validate TTML:', err);
    }
  };

  const validateAllTtmlFiles = async () => {
    if (!sessionId || !token) return;
    
    setIsValidating(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:7777/api'}/sessions/${sessionId}/validate-ttml`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to validate metadata revisions');
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log('[TTMLRanking] Validation result:', result.summary);
        setNotification(`Validation complete: ${result.summary.validFiles}/${result.summary.totalFiles} files are valid`);

        // Apply backend validation results to file cards
        if (result.results && result.results.length > 0) {
          setAllTtmlFiles(prev => {
            const updated = prev.map(file => {
              const backendResult = result.results.find(
                (r: any) => r.checksum && (r.checksum === file.checksum || r.checksum === file.id)
              );
              if (backendResult) {
                return {
                  ...file,
                  isValid: backendResult.isValid,
                  validationErrors: backendResult.errors || [],
                  validationWarnings: backendResult.warnings || [],
                  validationStats: backendResult.stats || null,
                  lastValidated: new Date().toISOString(),
                };
              }
              return file;
            });
            // Also update the parent with the validated files
            updateParentWithAllTtmlFiles(updated);
            return updated;
          });
        }
      }
    } catch (err) {
      console.error('Failed to validate TTML files:', err);
      setError('Failed to validate metadata revisions');
    } finally {
      setIsValidating(false);
    }
  };

  // Handle TTML file upload (from DaVinci Resolve or other external sources)
  const handleTtmlUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !sessionId || !token) return;

    // Clear previous results
    setUploadResult(null);
    setIsUploading(true);
    setError('');

    try {
      // Read file content
      const content = await file.text();

      // Basic client-side validation
      if (!content.includes('<tt') || !content.includes('</tt>')) {
        setUploadResult({
          success: false,
          message: 'Invalid file: Not a valid TTML/XML document'
        });
        return;
      }

      console.log('[TTML-UPLOAD] Uploading file:', file.name, 'size:', content.length);

      // Get total duration from transcription data if available
      const totalDuration = transcriptionData?.results?.channels?.[0]?.alternatives?.[0]?.words?.slice(-1)?.[0]?.end || null;

      // Upload to server
      const apiBase = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:7777/api';
      const response = await fetch(`${apiBase}/sessions/${sessionId}/upload-ttml`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ttmlContent: content,
          filename: file.name,
          totalDuration
        })
      });

      const result = await response.json();

      if (result.success) {
        setUploadResult({
          success: true,
          message: result.message,
          checksum: result.checksum,
          isValid: result.isValid,
          validationErrors: result.validationErrors,
          validationWarnings: result.validationWarnings
        });

        // Add the uploaded TTML to the list
        // Use the processed content from server (which includes Resolve conversion if needed)
        const processedContent = result.ttmlContent || content;
        const newTtmlFile: EnhancedTTMLFile = {
          id: result.checksum,
          name: file.name,
          content: processedContent,
          type: 'cached',
          source: 'standard',
          model: 'external-upload',
          generatedAt: new Date(),
          version: 1,
          checksum: result.checksum,
          isValid: result.isValid,
          validationErrors: result.validationErrors,
          validationWarnings: result.validationWarnings,
          validationStats: result.validationStats,
          isDraft: Boolean((result as any).isDraft)
        };

        setAllTtmlFiles(prev => {
          // Check if this checksum already exists
          if (prev.some(f => f.checksum === result.checksum)) {
            return prev;
          }
          return [...prev, newTtmlFile];
        });

        // Show notification
        if (result.isValid) {
          setNotification(`TTML imported successfully: ${file.name}`);
        } else {
          setNotification(`TTML imported with ${result.validationErrors?.length || 0} validation error(s). Use follow-up fixes to resolve them.`);
        }

        // Update session with new TTML file
        if (onTtmlGenerated) {
          const updatedFiles = [...allTtmlFiles.filter(f => f.checksum !== result.checksum), newTtmlFile];
          onTtmlGenerated(updatedFiles.map(f => ({
            name: f.name,
            content: f.content || '',
            filename: f.name,
            generatedAt: f.generatedAt,
            checksum: f.checksum,
            isValid: f.isValid,
            validationErrors: f.validationErrors,
            validationWarnings: f.validationWarnings,
            validationStats: f.validationStats
          })));
        }
      } else {
        setUploadResult({
          success: false,
          message: result.error || 'Upload failed'
        });
        setError(result.error || 'Failed to upload TTML');
      }
    } catch (err) {
      console.error('[TTML-UPLOAD] Error:', err);
      setUploadResult({
        success: false,
        message: err instanceof Error ? err.message : 'Upload failed'
      });
      setError('Failed to upload metadata revision');
    } finally {
      setIsUploading(false);
      // Reset file input so the same file can be uploaded again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const generateTTML = async (options?: {
    topicDensity?: number;
    enableWebSearch?: boolean;
    includeEdits?: boolean;
    additionalInstructions?: string;
  }) => {
    const {
      topicDensity = 18,
      enableWebSearch = false,
      includeEdits = !isPublishedOrYoutubeLinkedSession,
      additionalInstructions = ''
    } = options || {};

    // Check share permissions
    if (sharePermissions && !sharePermissions.rankingTtmlAllowed) {
      setError('Metadata generation is not allowed for this share link');
      return;
    }

    if (!sessionId) {
      setError('Session ID is required for TTML generation');
      return;
    }

    // Check if we're at the generation limit (legacy jobs + active agent workflows)
    const activeServerJobs = serverJobs.filter(j => j.status === 'pending' || j.status === 'generating' || j.status === 'refining').length;
    const activeCount = activeServerJobs + activeAgentWorkflowCount;

    if (activeCount >= MAX_CONCURRENT_GENERATIONS) {
      setError(`Maximum ${MAX_CONCURRENT_GENERATIONS} concurrent generations allowed`);
      return;
    }

    try {
      console.log('[TTML Agent] Starting validator-gated TTML generation...', 'Model:', selectedRefinementModel, 'Topic density:', topicDensity, 'Web verification:', enableWebSearch, 'Include edits:', includeEdits, 'Additional instructions:', additionalInstructions ? 'yes' : 'no');

      const response = await startTtmlGenerationSession({
        sessionId,
        model: selectedRefinementModel,
        topicDensity,
        enableWebAccess: enableWebSearch,
        includeEdits,
        additionalInstructions
      }, token || undefined);

      if (response.success) {
        console.log('[TTML Agent] Generation session started:', response.agentSessionId);
        trackAgentWorkflow(response.agentSessionId, 'generation');
        loadAgentWorkflows();
        setNotification('Validator-gated metadata generation started via agent');
      } else {
        throw new Error('Failed to start metadata generation session');
      }
    } catch (err: any) {
      console.error('[TTML Agent] Failed to start generation session:', err);
      setError(err.message || 'Failed to start metadata generation session');
    }
  };

  const deleteJob = async (jobId: string) => {
    if (!sessionId || !token) return;

    try {
      const response = await openRouterService.deleteTtmlJob(sessionId, jobId, token);
      if (response.success) {
        setNotification('Job deleted successfully');
        // Refresh job list
        const jobsResponse = await openRouterService.getTtmlJobs(sessionId, token);
        if (jobsResponse.success) {
          setServerJobs(jobsResponse.jobs);
        }
      } else {
        throw new Error('Failed to delete job');
      }
    } catch (err: any) {
      console.error('[TTML Jobs] Failed to delete job:', err);
      setError(err.message || 'Failed to delete job');
    }
  };

  const downloadTtmlFile = async (file: EnhancedTTMLFile) => {
    try {
      let blob: Blob;
      if (file.type === 'cached') {
        blob = await openRouterService.downloadCachedTTML(file.checksum!, token!);
      } else {
        blob = new Blob([file.content!], { type: 'application/ttml+xml' });
      }

      // Extract short model name for filename suffix (e.g., "anthropic/claude-sonnet-4" -> "claude-sonnet-4")
      const modelShortName = file.model?.split('/').pop() || 'unknown';

      // Build filename: base name without extension + model name + .ttml
      let baseName = file.name || `ttml-v${file.version || 'unknown'}`;
      // Remove any existing extension
      if (baseName.endsWith('.xml') || baseName.endsWith('.ttml')) {
        baseName = baseName.replace(/\.(xml|ttml)$/i, '');
      }
      // Append model name if not already present
      if (!baseName.includes(modelShortName)) {
        baseName = `${baseName}-${modelShortName}`;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseName}.ttml`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download TTML file:', err);
      setError('Failed to download metadata revision');
    }
  };

  const deleteTtmlFile = async (file: EnhancedTTMLFile) => {
    try {
      if (file.type === 'cached' && file.checksum) {
        await openRouterService.deleteCachedTTML(file.checksum, token!);
      }

      // Get remaining files after deletion
      const remainingFiles = allTtmlFiles.filter(f => f.id !== file.id);
      setAllTtmlFiles(remainingFiles);
      setNotification('Metadata revision deleted');

      // If this was the selected file, auto-select another one if available
      if (file.isSelected && sessionId) {
        // Find another valid TTML to select
        const validRemainingFiles = remainingFiles.filter(f => f.isValid !== false);

        if (validRemainingFiles.length > 0) {
          // Auto-select the first remaining valid TTML
          const nextFile = validRemainingFiles[0];
          const nextTtmlId = nextFile.checksum || nextFile.id;

          await sessionService.updateSession({
            sessionId: sessionId,
            selectedTtmlId: nextTtmlId
          }, token!);
          setSelectedTtmlId(nextTtmlId);

          // Update the isSelected property
          setAllTtmlFiles(prev => prev.map(f => ({
            ...f,
            isSelected: (f.checksum || f.id) === nextTtmlId
          })));

          // Call onTtmlSelected to update parent
          if (onTtmlSelected) {
            onTtmlSelected(nextTtmlId);
          }

          setNotification(`Metadata revision deleted. Auto-selected: ${nextFile.name}`);
        } else {
          // No remaining files, clear selection
          await sessionService.updateSession({
            sessionId: sessionId,
            selectedTtmlId: null
          }, token!);
          setSelectedTtmlId(null);

          // Call onTtmlPreview to clear preview in parent component
          if (onTtmlPreview) {
            onTtmlPreview(null);
          }
        }
      }
    } catch (err) {
      console.error('Failed to delete TTML file:', err);
      setError('Failed to delete metadata revision');
    }
  };

  // View TTML content in modal
  const openViewTtmlModal = async (file: EnhancedTTMLFile) => {
    try {
      let content: string;
      if (file.type === 'cached' && !file.content) {
        const blob = await openRouterService.downloadCachedTTML(file.checksum!, token!);
        content = await blob.text();
      } else {
        content = file.content || '';
      }
      setViewTtmlModal({ isOpen: true, file, content });
      setEditedContent(content);
      setHasEdits(false);
      setValidationError(null);
      setEditorTab('raw');
      // Parse TTML segments
      parseTtmlSegments(content);
    } catch (err) {
      console.error('Failed to load TTML content:', err);
      setError('Failed to load TTML content');
    }
  };

  // Helper to decode XML entities
  const decodeXmlEntities = (text: string): string => {
    return text
      .replace(/&gt;/g, '>')
      .replace(/&lt;/g, '<')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'");
  };

  // Helper to encode XML entities
  const encodeXmlEntities = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  // Parse clip content into structured fields
  // Supports both new format: [CLIP] Title | Type | Description | Visual
  // And old format: [CLIP] Title<br/>Type: description<br/>Visual: suggestion
  const parseClipContent = (rawText: string): Partial<TtmlSegment> => {
    let decoded = decodeXmlEntities(rawText)
      .replace(/\[CLIP\]\s*/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .trim();

    // Check for new pipe-delimited format
    if (decoded.includes('|') && !decoded.includes('\n')) {
      const parts = decoded.split('|').map(p => p.trim());
      return {
        clipTitle: parts[0] || '',
        clipType: parts[1] || '',
        clipDescription: parts[2] || '',
        clipVisual: parts[3] || ''
      };
    }

    // Old format: parse line by line
    const lines = decoded.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    let title = '';
    let type = '';
    let description = '';
    let visual = '';

    for (const line of lines) {
      const lowerLine = line.toLowerCase();
      if (lowerLine.startsWith('visual:')) {
        visual = line.substring(7).trim();
      } else if (lowerLine.includes('viral hook')) {
        type = 'Viral Hook';
        const colonIdx = line.indexOf(':');
        if (colonIdx > -1) description = line.substring(colonIdx + 1).trim();
      } else if (lowerLine.includes('educational nugget') || lowerLine.startsWith('educational:')) {
        type = 'Educational Nugget';
        const colonIdx = line.indexOf(':');
        if (colonIdx > -1) description = line.substring(colonIdx + 1).trim();
      } else if (lowerLine.includes('humorous aside') || lowerLine.startsWith('humorous:')) {
        type = 'Humorous Aside';
        const colonIdx = line.indexOf(':');
        if (colonIdx > -1) description = line.substring(colonIdx + 1).trim();
      } else if (!title) {
        title = line;
      } else if (!description) {
        description = line;
      }
    }

    return { clipTitle: title, clipType: type, clipDescription: description, clipVisual: visual };
  };

  // Parse edit content into structured fields
  // Supports both new format: [EDIT] Title | Suggestion | Improvement | Priority
  // And old format: [EDIT] Title<br/>Suggestion: ...<br/>Improvement: ...<br/>Priority: ...
  const parseEditContent = (rawText: string): Partial<TtmlSegment> => {
    let decoded = decodeXmlEntities(rawText)
      .replace(/\[EDIT\]\s*/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .trim();

    // Check for new pipe-delimited format (no line breaks, has pipes)
    if (decoded.includes('|') && !decoded.includes('\n')) {
      const parts = decoded.split('|').map(p => p.trim());
      return {
        editTitle: parts[0] || '',
        editSuggestion: parts[1] || '',
        editImprovement: parts[2] || '',
        editPriority: parts[3] || ''
      };
    }

    // Old format: parse line by line
    const lines = decoded.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    let title = '';
    let suggestion = '';
    let improvement = '';
    let priority = '';

    for (const line of lines) {
      if (line.toLowerCase().startsWith('suggestion:')) {
        suggestion = line.substring(11).trim();
      } else if (line.toLowerCase().startsWith('improvement:')) {
        improvement = line.substring(12).trim();
      } else if (line.toLowerCase().startsWith('priority:')) {
        priority = line.substring(9).trim();
      } else if (!title) {
        title = line;
      }
    }

    return { editTitle: title, editSuggestion: suggestion, editImprovement: improvement, editPriority: priority };
  };

  // Parse reference content into structured fields
  // Format: [REF] Name | Link Text | URL<br/>Category<br/>Context<br/>Visual
  // Or: [REF] Name<br/>Category<br/>Context<br/>Visual
  const parseReferenceContent = (rawText: string): Partial<TtmlSegment> => {
    let decoded = decodeXmlEntities(rawText)
      .replace(/\[REF\]\s*/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .trim();

    const lines = decoded.split('\n').map(l => l.trim()).filter(l => l.length > 0);

    let name = '';
    let linkText = '';
    let url = '';
    let category = '';
    let context = '';
    let visual = '';

    if (lines.length > 0) {
      const firstLine = lines[0];
      // Check for pipe format in first line
      if (firstLine.includes('|')) {
        const parts = firstLine.split('|').map(p => p.trim());
        name = parts[0] || '';
        linkText = parts[1] || '';
        url = parts[2] || '';
      } else {
        name = firstLine;
      }

      // Parse remaining lines
      if (lines.length > 1) category = lines[1];
      if (lines.length > 2) context = lines[2];
      if (lines.length > 3) visual = lines[3];
    }

    return { refName: name, refLinkText: linkText, refUrl: url, refCategory: category, refContext: context, refVisual: visual };
  };

  // Parse topic content into structured fields
  // Format: [1: 20/20] >> [Title]<br/>Score: X/10 (...)<br/>Justification: ...<br/>Optimal Placement: [X/20]<br/>Continuity: ...
  const parseTopicContent = (rawText: string): Partial<TtmlSegment> => {
    const decoded = decodeXmlEntities(rawText).replace(/<br\s*\/?>/gi, '\n').trim();
    const lines = decoded.split('\n').map(l => l.trim()).filter(l => l.length > 0);

    let rank = '';
    let placement = '';
    let title = '';
    let score = '';
    let justification = '';
    let optimalPlacement = '';
    let continuity = '';

    // First line: [1: 20/20] >> [Title] or [1: ?/9] >> Title
    if (lines.length > 0) {
      const firstLine = lines[0];
      // Match [rank: placement] >> [title] or [rank: placement] >> title (? = unrated)
      const headerMatch = firstLine.match(/^\[(\d+):\s*([\d?]+\/?\d*)\]\s*>>\s*\[?([^\]]+)\]?/);
      if (headerMatch) {
        rank = headerMatch[1];
        placement = headerMatch[2];
        title = headerMatch[3].trim();
      }
    }

    // Parse remaining lines
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (line.toLowerCase().startsWith('score:')) {
        score = line.replace(/^score:\s*/i, '').trim();
      } else if (line.toLowerCase().startsWith('justification:')) {
        justification = line.replace(/^justification:\s*/i, '').trim();
      } else if (line.toLowerCase().startsWith('optimal placement:')) {
        optimalPlacement = line.replace(/^optimal placement:\s*/i, '').replace(/[\[\]]/g, '').trim();
      } else if (line.toLowerCase().startsWith('continuity:')) {
        continuity = line.replace(/^continuity:\s*/i, '').trim();
      }
    }

    return {
      topicRank: rank,
      topicPlacement: placement,
      topicTitle: title,
      topicScore: score,
      topicJustification: justification,
      topicOptimalPlacement: optimalPlacement,
      topicContinuity: continuity
    };
  };

  // Parse TTML content into editable segments
  const parseTtmlSegments = (content: string) => {
    const parseDiv = (divId: string, type: 'topic' | 'clip' | 'edit' | 'reference'): TtmlSegment[] => {
      const segments: TtmlSegment[] = [];
      const divRegex = new RegExp(`<div[^>]*xml:id="${divId}"[^>]*>([\\s\\S]*?)<\\/div>`);
      const divMatch = content.match(divRegex);
      if (divMatch) {
        const pRegex = /<p[^>]*begin="([^"]+)"[^>]*end="([^"]+)"[^>]*>([\s\S]*?)<\/p>/g;
        let match;
        let idx = 0;
        while ((match = pRegex.exec(divMatch[1])) !== null) {
          const rawText = match[3];
          const plainText = rawText.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();

          let structuredFields: Partial<TtmlSegment> = {};
          if (type === 'topic') {
            structuredFields = parseTopicContent(rawText);
          } else if (type === 'clip') {
            structuredFields = parseClipContent(rawText);
          } else if (type === 'edit') {
            structuredFields = parseEditContent(rawText);
          } else if (type === 'reference') {
            structuredFields = parseReferenceContent(rawText);
          }

          segments.push({
            id: `${divId}_${idx}`,
            begin: match[1],
            end: match[2],
            text: plainText,
            originalXml: match[0],
            ...structuredFields
          });
          idx++;
        }
      }
      return segments;
    };

    setParsedSegments({
      topics: parseDiv('d_summary', 'topic'),
      edits: parseDiv('d_edit', 'edit'),
      clips: parseDiv('d_clip', 'clip'),
      references: parseDiv('d_references', 'reference')
    });
  };

  // Update a segment's text (for raw text editing)
  const updateSegmentText = (type: keyof typeof parsedSegments, segmentId: string, newText: string) => {
    setParsedSegments(prev => ({
      ...prev,
      [type]: prev[type].map(seg =>
        seg.id === segmentId ? { ...seg, text: newText } : seg
      )
    }));
    setHasEdits(true);
  };

  // Update a specific structured field in a segment
  const updateSegmentField = (
    type: keyof typeof parsedSegments,
    segmentId: string,
    field: keyof TtmlSegment,
    value: string
  ) => {
    setParsedSegments(prev => ({
      ...prev,
      [type]: prev[type].map(seg =>
        seg.id === segmentId ? { ...seg, [field]: value } : seg
      )
    }));
    setHasEdits(true);
  };

  // Update segment timestamps
  const updateSegmentTimestamp = (
    type: keyof typeof parsedSegments,
    segmentId: string,
    field: 'begin' | 'end',
    value: string
  ) => {
    setParsedSegments(prev => ({
      ...prev,
      [type]: prev[type].map(seg =>
        seg.id === segmentId ? { ...seg, [field]: value } : seg
      )
    }));
    setHasEdits(true);
  };

  // Delete a segment
  const deleteSegment = (type: keyof typeof parsedSegments, segmentId: string) => {
    setParsedSegments(prev => ({
      ...prev,
      [type]: prev[type].filter(seg => seg.id !== segmentId)
    }));
    setHasEdits(true);
  };

  // Build clip content in pipe format
  const buildClipContent = (seg: TtmlSegment): string => {
    const parts = [
      `[CLIP] ${encodeXmlEntities(seg.clipTitle || '')}`,
      encodeXmlEntities(seg.clipType || ''),
      encodeXmlEntities(seg.clipDescription || ''),
      encodeXmlEntities(seg.clipVisual || '')
    ];
    return parts.join(' | ');
  };

  // Build edit content in pipe format
  const buildEditContent = (seg: TtmlSegment): string => {
    const parts = [
      `[EDIT] ${encodeXmlEntities(seg.editTitle || '')}`,
      encodeXmlEntities(seg.editSuggestion || ''),
      encodeXmlEntities(seg.editImprovement || ''),
      encodeXmlEntities(seg.editPriority || '')
    ];
    return parts.join(' | ');
  };

  // Build reference content with proper formatting
  const buildReferenceContent = (seg: TtmlSegment): string => {
    let firstLine = `[REF] ${encodeXmlEntities(seg.refName || '')}`;
    if (seg.refLinkText && seg.refUrl) {
      firstLine += ` | ${encodeXmlEntities(seg.refLinkText)} | ${encodeXmlEntities(seg.refUrl)}`;
    }
    const lines = [firstLine];
    if (seg.refCategory) lines.push(encodeXmlEntities(seg.refCategory));
    if (seg.refContext) lines.push(encodeXmlEntities(seg.refContext));
    if (seg.refVisual) lines.push(encodeXmlEntities(seg.refVisual));
    return lines.join('<br/>');
  };

  // Build topic content from structured fields
  const buildTopicContent = (seg: TtmlSegment): string => {
    const lines: string[] = [];
    // First line: [rank: placement] >> [title]
    const headerLine = `[${seg.topicRank || '?'}: ${seg.topicPlacement || '?'}] &gt;&gt; [${encodeXmlEntities(seg.topicTitle || '')}]`;
    lines.push(headerLine);
    if (seg.topicScore) lines.push(`Score: ${encodeXmlEntities(seg.topicScore)}`);
    if (seg.topicJustification) lines.push(`Justification: ${encodeXmlEntities(seg.topicJustification)}`);
    if (seg.topicOptimalPlacement) lines.push(`Optimal Placement: [${encodeXmlEntities(seg.topicOptimalPlacement)}]`);
    if (seg.topicContinuity) lines.push(`Continuity: ${encodeXmlEntities(seg.topicContinuity)}`);
    return lines.join('<br/>');
  };

  // Adjust topic times to ensure contiguous segments with no gaps/overlaps
  const adjustContiguousTopicTimes = (segmentId: string, field: 'begin' | 'end', newValue: string) => {
    setParsedSegments(prev => {
      const topics = [...prev.topics];
      const idx = topics.findIndex(t => t.id === segmentId);
      if (idx === -1) return prev;

      // Update the target segment
      topics[idx] = { ...topics[idx], [field]: newValue };

      // If changing end time, update next topic's begin to match
      if (field === 'end' && idx < topics.length - 1) {
        topics[idx + 1] = { ...topics[idx + 1], begin: newValue };
      }
      // If changing begin time, update previous topic's end to match
      if (field === 'begin' && idx > 0) {
        topics[idx - 1] = { ...topics[idx - 1], end: newValue };
      }

      return { ...prev, topics };
    });
    setHasEdits(true);
  };

  // Rebuild TTML from edited segments
  const rebuildTtmlFromSegments = (): string => {
    let newContent = editedContent;

    // Rebuild topics using structured fields when available
    const rebuildTopicsDiv = (segments: TtmlSegment[]) => {
      const newParagraphs = segments.map(seg => {
        // Use structured fields if available, otherwise fall back to raw text
        const content = seg.topicTitle !== undefined ? buildTopicContent(seg) : seg.text.replace(/\n/g, '<br/>');
        return `      <p begin="${seg.begin}" end="${seg.end}">${content}</p>`;
      }).join('\n');
      const newDivContent = `    <div xml:id="d_summary" region="r_summary" style="s0">\n${newParagraphs}\n    </div>`;
      const divRegex = /<div[^>]*xml:id="d_summary"[^>]*>[\s\S]*?<\/div>/;
      newContent = newContent.replace(divRegex, newDivContent);
    };

    // Rebuild clips with pipe format
    const rebuildClipsDiv = (segments: TtmlSegment[]) => {
      const newParagraphs = segments.map(seg => {
        const content = seg.clipTitle !== undefined ? buildClipContent(seg) : seg.text.replace(/\n/g, '<br/>');
        return `      <p begin="${seg.begin}" end="${seg.end}">${content}</p>`;
      }).join('\n');
      const newDivContent = `    <div xml:id="d_clip" region="r_clip" style="s1">\n${newParagraphs}\n    </div>`;
      const divRegex = /<div[^>]*xml:id="d_clip"[^>]*>[\s\S]*?<\/div>/;
      newContent = newContent.replace(divRegex, newDivContent);
    };

    // Rebuild edits with pipe format
    const rebuildEditsDiv = (segments: TtmlSegment[]) => {
      const newParagraphs = segments.map(seg => {
        const content = seg.editTitle !== undefined ? buildEditContent(seg) : seg.text.replace(/\n/g, '<br/>');
        return `      <p begin="${seg.begin}" end="${seg.end}">${content}</p>`;
      }).join('\n');
      const newDivContent = `    <div xml:id="d_edit" region="r_edit" style="s2">\n${newParagraphs}\n    </div>`;
      const divRegex = /<div[^>]*xml:id="d_edit"[^>]*>[\s\S]*?<\/div>/;
      newContent = newContent.replace(divRegex, newDivContent);
    };

    // Rebuild references with proper format
    const rebuildReferencesDiv = (segments: TtmlSegment[]) => {
      const newParagraphs = segments.map(seg => {
        const content = seg.refName !== undefined ? buildReferenceContent(seg) : seg.text.replace(/\n/g, '<br/>');
        return `      <p begin="${seg.begin}" end="${seg.end}">${content}</p>`;
      }).join('\n');
      const newDivContent = `    <div xml:id="d_references" region="r_references" style="s3">\n${newParagraphs}\n    </div>`;
      const divRegex = /<div[^>]*xml:id="d_references"[^>]*>[\s\S]*?<\/div>/;
      newContent = newContent.replace(divRegex, newDivContent);
    };

    rebuildTopicsDiv(parsedSegments.topics);
    rebuildClipsDiv(parsedSegments.clips);
    rebuildEditsDiv(parsedSegments.edits);
    rebuildReferencesDiv(parsedSegments.references);

    return newContent;
  };

  // Validate and save edited TTML as new version
  const saveEditedTtml = async () => {
    if (!viewTtmlModal.file || !sessionId || !token) return;

    // Prevent double-click race condition using ref (synchronous check)
    if (isSavingRef.current) {
      console.log('[TTML-SAVE] Ignoring duplicate save request - already saving');
      return;
    }
    isSavingRef.current = true;

    setIsSaving(true);
    setValidationError(null);

    try {
      // Rebuild TTML from segments if on segment tab, otherwise use raw edited content
      const ttmlToSave = editorTab === 'raw' ? editedContent : rebuildTtmlFromSegments();

      // Call backend to validate and save
      const response = await fetch(`${process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:7777/api'}/save-edited-ttml`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId,
          parentChecksum: viewTtmlModal.file.checksum,
          editedTtml: ttmlToSave,
          model: viewTtmlModal.file.model
        })
      });

      const result = await response.json();

      if (!result.success) {
        setValidationError(result.error || 'Validation failed');
        return;
      }

      // Success - close modal and reload files
      setNotification(`Saved as new version (v${result.version})`);
      setViewTtmlModal({ isOpen: false, file: null, content: '' });
      setHasEdits(false);
      loadAllTtmlFiles();

    } catch (err: any) {
      console.error('Failed to save edited TTML:', err);
      setValidationError(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  };

  // Copy TTML content to clipboard
  const copyTtmlToClipboard = async (file: EnhancedTTMLFile) => {
    try {
      let content: string;
      if (file.type === 'cached' && !file.content) {
        const blob = await openRouterService.downloadCachedTTML(file.checksum!, token!);
        content = await blob.text();
      } else {
        content = file.content || '';
      }
      await copyToClipboard(content);
      setNotification('Raw TTML copied to clipboard');
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      console.error('Failed to copy TTML:', err);
      setError('Failed to copy TTML to clipboard');
    }
  };

  // Open refine modal
  const openRefineModal = (file: EnhancedTTMLFile) => {
    setRefineModal({
      isOpen: true,
      file,
      instructions: '',
      iterationCount: 1,
      blankSlate: false,
      maxIssuesToFix: 0,  // Default to unlimited
      topicDensity: 18  // Default topic density
    });
  };

  // Start a follow-up TTML agent session for targeted improvements
  const submitRefinement = async () => {
    if (!refineModal.file || !sessionId || !refineModal.file.checksum) {
      return;
    }

    const { file, instructions, iterationCount, blankSlate, maxIssuesToFix, topicDensity } = refineModal;
    setRefineModal(prev => ({ ...prev, isOpen: false }));
    setError('');

    const notificationMsg = `Starting metadata follow-up agent${blankSlate ? ' (blank slate)' : ''}${maxIssuesToFix === 0 ? ' (fix all issues)' : ` (fix up to ${maxIssuesToFix} issues)`} (${iterationCount} iteration${iterationCount > 1 ? 's' : ''})...`;
    setNotification(notificationMsg);

    // Add to refining files set
    setRefiningFiles(prev => new Set(prev).add(file.checksum!));

    try {
      const agentResult = await startTtmlRefinementSession({
        sessionId,
        sourceChecksum: file.checksum!,
        model: selectedRefinementModel,
        additionalInstructions: instructions,
        enableWebAccess,
        iterationCount,
        blankSlate,
        maxIssuesToFix,
        topicDensity,
      }, token || undefined);

      if (agentResult.success) {
        console.log(`[REFINE-AGENT] Started refinement agent session: ${agentResult.agentSessionId}`);
        trackAgentWorkflow(agentResult.agentSessionId, 'follow-up');
        loadAgentWorkflows();
        const successMsg = `Metadata follow-up started via agent (${iterationCount} iteration${iterationCount > 1 ? 's' : ''})`;
        setNotification(successMsg);
      } else {
        throw new Error('Agent session returned failure');
      }
    } catch (err) {
      console.error('Failed to start TTML follow-up agent session:', err);
      setError(err instanceof Error ? err.message : 'Failed to start TTML follow-up agent session');
      setRefiningFiles(prev => {
        const next = new Set(prev);
        next.delete(file.checksum!);
        return next;
      });
    }
  };


  // Calculate version deltas - compare each version to its parent
  const calculateVersionDeltas = useMemo(() => {
    const deltas: Record<string, {
      topics: number;
      clips: number;
      edits: number;
      refs: number;
      hasParent: boolean;
    }> = {};

    // Build a lookup map by checksum for finding parents
    const filesByChecksum: Record<string, EnhancedTTMLFile> = {};
    allTtmlFiles.forEach(f => {
      if (f.checksum) {
        filesByChecksum[f.checksum] = f;
      }
    });

    // Calculate deltas for each file
    allTtmlFiles.forEach(file => {
      const stats = file.validationStats?.segmentsByType || {};
      const currentCounts = {
        topics: stats.topic || 0,
        clips: stats.clip || 0,
        edits: stats.edit || 0,
        refs: stats.reference || 0
      };

      // Find parent file by parentChecksum
      let parentStats = { topic: 0, clip: 0, edit: 0, reference: 0 };
      let hasParent = false;

      if (file.parentChecksum) {
        const parentFile = filesByChecksum[file.parentChecksum];
        if (parentFile?.validationStats?.segmentsByType) {
          parentStats = parentFile.validationStats.segmentsByType;
          hasParent = true;
        }
      }

      deltas[file.id] = {
        topics: currentCounts.topics - (parentStats.topic || 0),
        clips: currentCounts.clips - (parentStats.clip || 0),
        edits: currentCounts.edits - (parentStats.edit || 0),
        refs: currentCounts.refs - (parentStats.reference || 0),
        hasParent
      };
    });

    return deltas;
  }, [allTtmlFiles]);

  // Group files by version chain (rootChecksum) and pick representative for each chain
  const groupedTtmlFiles = useMemo(() => {
    console.log('[TTMLGrouping] Grouping', visibleTtmlFiles.length, 'visible files by rootChecksum');

    // Create a map of rootChecksum -> all versions in that chain
    const chains = new Map<string, EnhancedTTMLFile[]>();

    for (const file of visibleTtmlFiles) {
      const rootKey = file.rootChecksum || file.checksum || file.id;
      console.log('[TTMLGrouping] File', file.checksum?.substring(0, 8), 'version:', file.version, 'rootChecksum:', file.rootChecksum?.substring(0, 8), '-> rootKey:', rootKey?.substring(0, 8));
      if (!chains.has(rootKey)) {
        chains.set(rootKey, []);
      }
      chains.get(rootKey)!.push(file);
    }

    // Log chain summary
    console.log('[TTMLGrouping] Chains found:', chains.size, 'chains with versions:',
      [...chains.entries()].map(([key, versions]) => `${key?.substring(0, 8)}:${versions.length}v`).join(', '));

    // For each chain, pick the representative to display
    // Priority: viewedVersions state > selected TTML > latest version
    const representatives: EnhancedTTMLFile[] = [];

    for (const [rootKey, versions] of chains) {
      // Sort versions by version number
      versions.sort((a, b) => (a.version || 1) - (b.version || 1));

      // Check if user has explicitly selected a version to view for this chain
      const viewedChecksum = viewedVersions[rootKey];
      const viewedVersion = viewedChecksum ? versions.find(v => v.checksum === viewedChecksum) : null;

      // Check if any version in this chain is the globally selected TTML
      const selectedInChain = versions.find(v => v.isSelected);

      // Pick representative: viewedVersions state > selected TTML > latest version
      let representative: EnhancedTTMLFile;

      if (viewedVersion) {
        // User explicitly selected this version via orb click
        representative = viewedVersion;
      } else if (selectedInChain) {
        // If a version in this chain is the globally selected TTML, show that
        representative = selectedInChain;
      } else {
        // Otherwise show the latest version (highest version number)
        representative = versions[versions.length - 1];
      }

      // Attach chain info to the representative for version orb rendering
      (representative as any)._chainVersions = versions;
      (representative as any)._chainRootKey = rootKey;
      // Store the root version's creation time for stable sorting
      (representative as any)._rootCreatedAt = versions[0]?.generatedAt || representative.generatedAt;

      representatives.push(representative);
    }

    // Sort representatives by ROOT version creation date (most recent first)
    // This ensures order stays stable when switching between versions in a chain
    representatives.sort((a, b) => {
      const dateA = new Date((a as any)._rootCreatedAt || a.generatedAt || 0).getTime();
      const dateB = new Date((b as any)._rootCreatedAt || b.generatedAt || 0).getTime();
      return dateB - dateA;
    });

    return representatives;
  }, [visibleTtmlFiles, viewedVersions]);

  const formatAgentWorkflowTime = (value: string) => {
    const normalized = value && !value.endsWith('Z') ? `${value}Z` : value;
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  if (loadingModels) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#FFB52B]" />
      </div>
    );
  }

  const metadataHeaderClass = isDarkMode
    ? 'flex-shrink-0 rounded-[24px] border border-[#344052] bg-[#202A3A]/92 p-6 text-[#F4F4F4] shadow-[0_26px_48px_-32px_rgba(10,14,20,0.78)]'
    : 'flex-shrink-0 rounded-[24px] border border-[#D6DCE4] bg-white/[0.94] p-6 text-[#232E3F] shadow-[0_24px_44px_-30px_rgba(35,46,63,0.28)]';
  const metadataDescriptionClass = isDarkMode ? 'mb-4 hidden text-sm text-gray-300 md:block' : 'mb-4 hidden text-sm text-gray-600 md:block';
  const metadataSecondaryButtonClass = isDarkMode
    ? 'flex items-center rounded-md border border-[#445066] bg-[#253247] px-4 py-2 text-[#F4F4F4] transition-colors hover:bg-[#2E3D55] disabled:cursor-not-allowed disabled:opacity-50'
    : 'flex items-center rounded-md border border-[#CBD4E1] bg-[#F7F9FC] px-4 py-2 text-[#232E3F] transition-colors hover:bg-[#EBF0F6] disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-200px)]">
      {/* Header - Fixed at top */}
      <div className={metadataHeaderClass}>
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FileText className={`mr-2 h-5 w-5 ${isDarkMode ? 'text-[#FFCF74]' : 'text-[#B57700]'}`} />
          Metadata Generation and Management
        </h2>
        {!sharePermissions && (
          <p className={metadataDescriptionClass}>
            Validator-gated agent generation is the default path. Use follow-up agent sessions only for targeted edits or timeline-cut passes on an existing metadata revision.
          </p>
        )}

        {/* Action Buttons - Hide for public shares */}
        {!sharePermissions && (
          <>
            <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setGenerateModal({ isOpen: true, topicDensity: 18, enableWebSearch: false, includeEdits: !isPublishedOrYoutubeLinkedSession, additionalInstructions: '' })}
            disabled={
              serverJobs.filter(j => j.status === 'pending' || j.status === 'generating' || j.status === 'refining').length + activeAgentWorkflowCount >= MAX_CONCURRENT_GENERATIONS ||
              !selectedRefinementModel ||
              !transcript ||
              !sessionId
            }
            className="flex items-center rounded-md border border-[#E5A117] bg-[#FFB52B] px-4 py-2 text-[#232E3F] shadow-[0_18px_32px_-24px_rgba(255,181,43,0.9)] transition-colors hover:bg-[#FFC54D] dark:border-[#FFCF74] dark:bg-[#FFCF74] dark:text-[#1C2432] dark:hover:bg-[#FFD987] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Zap className="h-4 w-4 mr-2" />
            Generate With Agent
          </button>

          <button
            onClick={() => setShowMcpModal(true)}
            className={metadataSecondaryButtonClass}
            title="MLST ReScript MCP setup"
          >
            <Terminal className="h-4 w-4 mr-2" />
            MCP Setup
          </button>

          {/* Upload TTML Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || !sessionId}
            className={metadataSecondaryButtonClass}
            title="Import TTML exported from DaVinci Resolve or other editors"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Import TTML
              </>
            )}
          </button>
          {/* Hidden file input for TTML upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".ttml,.xml"
            onChange={handleTtmlUpload}
            className="hidden"
          />
            </div>
          </>
        )}

        {/* Upload Result Notification */}
        {uploadResult && (
          <div className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${
            uploadResult.success
              ? uploadResult.isValid
                ? 'border border-[#E7B14C]/45 bg-[#FFF4D8] dark:border-[#8E6E33] dark:bg-[#332A17]'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}>
            {uploadResult.success ? (
              uploadResult.isValid ? (
                <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B57700] dark:text-[#FFCF74]" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              )
            ) : (
              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-medium ${
                uploadResult.success
                  ? uploadResult.isValid
                    ? 'text-[#8B5B00] dark:text-[#FFE3A1]'
                    : 'text-yellow-800 dark:text-yellow-200'
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {uploadResult.message}
              </p>
              {uploadResult.validationErrors && uploadResult.validationErrors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium mb-1">
                    Validation errors ({uploadResult.validationErrors.length}):
                  </p>
                  <ul className="text-sm text-yellow-600 dark:text-yellow-400 list-disc list-inside space-y-0.5">
                    {uploadResult.validationErrors.slice(0, 5).map((err: any, i: number) => (
                      <li key={i}>{err.message || String(err)}</li>
                    ))}
                    {uploadResult.validationErrors.length > 5 && (
                      <li className="italic">...and {uploadResult.validationErrors.length - 5} more</li>
                    )}
                  </ul>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2 italic">
                    Use an agent follow-up session on the uploaded TTML to fix these issues and re-validate.
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => setUploadResult(null)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Public Share Notice */}
        {sharePermissions && (
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Metadata generation is not available for public shares. You are viewing the existing metadata revisions for this session.
            </p>
          </div>
        )}

        {!sharePermissions && (recentAgentGenerationSessions.length > 0 || loadingAgentWorkflows) && (
          <div className="mt-4 rounded-2xl border border-[#D6DCE4] bg-[#FFF8EA]/80 dark:border-[#445166] dark:bg-[#202A3A]/88">
            <button
              type="button"
              onClick={() => setShowRecentAgentGenerations(prev => !prev)}
              className="w-full flex items-start justify-between gap-3 px-3 py-2.5 text-left"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap text-sm font-medium text-gray-700 dark:text-gray-200">
                  <Zap className="h-4 w-4 text-[#B57700] dark:text-[#FFCF74]" />
                  <span>Recent Agent Generations</span>
                  <span className="inline-flex items-center rounded-full border border-[#FFB52B]/35 bg-[#FFF4D8] px-2 py-0.5 text-[11px] font-medium text-[#8B5B00] dark:border-[#FFB52B]/30 dark:bg-[#FFB52B]/[0.16] dark:text-[#FFE3A1]">
                    {activeAgentGenerationCount > 0
                      ? `${activeAgentGenerationCount} active`
                      : `${recentAgentGenerationSessions.length} recent`}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Validator-gated metadata generation runs for this episode. Follow-up edits stay in the Agent tab.
                </p>
              </div>
              <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
                {loadingAgentWorkflows && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {showRecentAgentGenerations ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </button>

            <AnimatePresence initial={false}>
              {showRecentAgentGenerations && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden border-t border-gray-200 dark:border-gray-700"
                >
                  <div className="p-2 space-y-1.5">
                    {recentAgentGenerationSessions.map((workflow) => {
                      const isActive = workflow.status === 'active';
                      const statusClasses = workflow.status === 'completed'
                        ? 'bg-[#FFF4D8] border-[#E7B14C] text-[#8B5B00] dark:bg-[#332A17] dark:border-[#8E6E33] dark:text-[#FFE3A1]'
                        : workflow.status === 'failed'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300'
                        : workflow.status === 'stopped'
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300'
                        : 'bg-[#EEF1F3] border-[#C7D0DB] text-[#3F4E62] dark:bg-[#273244] dark:border-[#4F5C72] dark:text-[#D5DCE5]';

                      return (
                        <div
                          key={workflow.id}
                          className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-2.5 py-2"
                        >
                          <div className={`h-2 w-2 rounded-full flex-shrink-0 ${
                            workflow.status === 'completed'
                              ? 'bg-[#FFB52B]'
                              : workflow.status === 'failed'
                              ? 'bg-red-500'
                              : workflow.status === 'stopped'
                              ? 'bg-yellow-500'
                              : 'bg-[#232E3F] animate-pulse dark:bg-[#FFCF74]'
                          }`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                                {workflow.title || 'TTML agent workflow'}
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${statusClasses}`}>
                                {workflow.status}
                              </span>
                            </div>
                            <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">
                              {[(workflow.model || '').split('/').pop() || workflow.model, `Updated ${formatAgentWorkflowTime(workflow.updated_at)}`]
                                .filter(Boolean)
                                .join(' • ')}
                              {isActive
                                ? ' • Live progress in Agent tab'
                                : ` • $${(workflow.total_cost_usd || 0).toFixed(2)}`}
                            </div>
                          </div>
                          <button
                            onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-agent', { detail: { agentSessionId: workflow.id } }))}
                            className="shrink-0 rounded-md border border-[#445166] bg-[#232E3F] px-2 py-1 text-xs text-[#F4F4F4] transition-colors hover:bg-[#2F3B4E]"
                          >
                            Open
                          </button>
                        </div>
                      );
                    })}
                    {loadingAgentWorkflows && (
                      <div className="px-1 text-[11px] text-gray-500 dark:text-gray-400 flex items-center">
                        <Loader2 className="h-3 w-3 animate-spin mr-2" />
                        Refreshing generation status...
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Active Server Jobs Status */}
        {serverJobs.filter(j => j.status === 'pending' || j.status === 'generating' || j.status === 'refining' ||
          j.status === 'failed' ||  // Always show failed jobs so they can be deleted
          (j.status === 'completed' && Date.now() - new Date(j.completed_at || '').getTime() < 5000)
        ).length > 0 && (
          <div className="mt-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Activity className="h-4 w-4 mr-2" />
              Legacy Server Jobs ({serverJobs.filter(j => j.status === 'pending' || j.status === 'generating').length} generating{serverJobs.filter(j => j.status === 'refining').length > 0 ? `, ${serverJobs.filter(j => j.status === 'refining').length} refining` : ''}{serverJobs.filter(j => j.status === 'failed').length > 0 ? `, ${serverJobs.filter(j => j.status === 'failed').length} failed` : ''})
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Compatibility path only. New TTML creation should start with the validator-gated agent flow above.
            </p>
            <div className="space-y-2">
              {serverJobs
                .filter(j => j.status === 'pending' || j.status === 'generating' || j.status === 'refining' ||
                  j.status === 'failed' ||  // Always show failed jobs so they can be deleted
                  (j.status === 'completed' && Date.now() - new Date(j.completed_at || '').getTime() < 5000)
                )
                .map((job) => (
                <div
                  key={job.job_id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    job.status === 'generating'
                      ? 'bg-[#EEF1F3] border-[#C7D0DB] dark:bg-[#273244] dark:border-[#4F5C72]'
                      : job.status === 'refining'
                      ? 'bg-[#232E3F] border-[#445166] dark:bg-[#232E3F] dark:border-[#556176]'
                      : job.status === 'completed'
                      ? 'bg-[#FFF4D8] border-[#E7B14C] dark:bg-[#332A17] dark:border-[#8E6E33]'
                      : job.status === 'failed'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {job.status === 'generating' && (
                      <Loader2 className="h-4 w-4 animate-spin text-[#3F4E62] dark:text-[#D5DCE5]" />
                    )}
                    {job.status === 'refining' && (
                      <RefreshCw className="h-4 w-4 animate-spin text-[#FFCF74]" />
                    )}
                    {job.status === 'completed' && (
                      <CheckCircle className="h-4 w-4 text-[#B57700] dark:text-[#FFCF74]" />
                    )}
                    {job.status === 'failed' && (
                      <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    )}
                    {job.status === 'pending' && (
                      <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                    )}

                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {job.type === 'best-of-3' ? '3x Composite' : 'Standard'} Metadata Generation
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          (Server-side)
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {job.model.split('/').pop()} • {job.progress || (job.status === 'pending' ? 'Queued...' : 'Processing...')}
                      </div>
                      {job.status === 'refining' && job.current_refinement > 0 && (
                        <div className="mt-1 text-xs text-[#FFCF74]">
                          Refinement {job.current_refinement} of {job.max_refinements}
                        </div>
                      )}
                      {job.enable_web_access === 1 && (
                        <div className="mt-1 text-xs text-[#B57700] dark:text-[#FFCF74]">
                          Web access enabled
                        </div>
                      )}
                      {job.error_message && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {job.error_message}
                        </div>
                      )}
                      {job.status === 'completed' && job.cost_usd && (
                        <div className="mt-1 text-xs text-[#B57700] dark:text-[#FFCF74]">
                          Cost: ${job.cost_usd.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {job.started_at
                        ? job.completed_at
                          ? `${Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000)}s`
                          : `${Math.round((Date.now() - new Date(job.started_at).getTime()) / 1000)}s`
                        : 'Waiting...'
                      }
                    </div>
                    {/* Cancel/Delete button - show for all job states */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteJob(job.job_id);
                      }}
                      className={`p-1 transition-colors ${
                        job.status === 'pending' || job.status === 'generating' || job.status === 'refining'
                          ? 'text-gray-400 hover:text-orange-500 dark:hover:text-orange-400'
                          : 'text-gray-400 hover:text-red-500 dark:hover:text-red-400'
                      }`}
                      title={job.status === 'pending' || job.status === 'generating' || job.status === 'refining' ? 'Cancel job' : 'Delete job'}
                    >
                      {job.status === 'pending' || job.status === 'generating' || job.status === 'refining' ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Refinement Jobs Status */}
        {refinementJobs.filter(j => j.status === 'pending' || j.status === 'refining' ||
          j.status === 'failed' ||
          ((j.status === 'completed' || j.status === 'no_changes') && Date.now() - new Date(j.completed_at || '').getTime() < 10000)
        ).length > 0 && (
          <div className="mt-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <RefreshCw className="h-4 w-4 mr-2" />
              Legacy Metadata Follow-Up Jobs ({refinementJobs.filter(j => j.status === 'refining').length} active{refinementJobs.filter(j => j.status === 'pending').length > 0 ? `, ${refinementJobs.filter(j => j.status === 'pending').length} pending` : ''}{refinementJobs.filter(j => j.status === 'failed').length > 0 ? `, ${refinementJobs.filter(j => j.status === 'failed').length} failed` : ''})
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Keep these for older runs. For new work, generate with the agent and use targeted follow-up sessions only when you need specific fixes.
            </p>
            <div className="space-y-2">
              {refinementJobs
                .filter(j => j.status === 'pending' || j.status === 'refining' ||
                  j.status === 'failed' ||
                  ((j.status === 'completed' || j.status === 'no_changes') && Date.now() - new Date(j.completed_at || '').getTime() < 10000)
                )
                .map((job) => {
                  const resultChecksums = job.result_checksums ? JSON.parse(job.result_checksums) : [];
                  const noChangeIterations = job.no_change_iterations ? JSON.parse(job.no_change_iterations) : [];
                  return (
                    <div
                      key={job.job_id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                        job.status === 'refining'
                          ? 'bg-[#232E3F] border-[#445166] dark:bg-[#232E3F] dark:border-[#556176]'
                          : job.status === 'completed'
                          ? 'bg-[#FFF4D8] border-[#E7B14C] dark:bg-[#332A17] dark:border-[#8E6E33]'
                          : job.status === 'no_changes'
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
                          : job.status === 'failed'
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                          : 'bg-gray-50 dark:bg-gray-900/20 border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        {job.status === 'refining' && (
                          <RefreshCw className="h-4 w-4 animate-spin text-[#FFCF74]" />
                        )}
                        {job.status === 'completed' && (
                          <CheckCircle className="h-4 w-4 text-[#B57700] dark:text-[#FFCF74]" />
                        )}
                        {job.status === 'no_changes' && (
                          <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                        )}
                        {job.status === 'failed' && (
                          <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                        )}
                        {job.status === 'pending' && (
                          <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        )}

                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Follow-Up Pass ({job.max_iterations} iteration{job.max_iterations > 1 ? 's' : ''})
                            <span className="ml-2 text-xs font-normal text-gray-500">
                              {job.source_checksum?.substring(0, 8)}...
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {job.model.split('-').slice(-2).join('-')} • {job.progress || (job.status === 'pending' ? 'Queued...' : 'Processing...')}
                          </div>
                          {job.status === 'refining' && job.current_iteration > 0 && (
                            <div className="mt-1 text-xs text-[#FFCF74]">
                              Iteration {job.current_iteration} of {job.max_iterations}
                            </div>
                          )}
                          {job.enable_web_access === 1 && (
                            <div className="mt-1 text-xs text-[#B57700] dark:text-[#FFCF74]">
                              Web access enabled
                            </div>
                          )}
                          {job.blank_slate === 1 && (
                            <div className="mt-1 text-xs text-[#3F4E62] dark:text-[#D5DCE5]">
                              Blank slate mode
                            </div>
                          )}
                          {job.status === 'completed' && (
                            <div className="mt-1 text-xs text-[#B57700] dark:text-[#FFCF74]">
                              Created {resultChecksums.length} version(s){noChangeIterations.length > 0 ? `, ${noChangeIterations.length} no-op` : ''} • ${(job.total_cost_usd || 0).toFixed(2)}
                            </div>
                          )}
                          {job.status === 'no_changes' && (
                            <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                              No changes detected in {noChangeIterations.length} iteration(s) • ${(job.total_cost_usd || 0).toFixed(2)}
                            </div>
                          )}
                          {job.error_message && (
                            <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                              {job.error_message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {job.started_at
                            ? job.completed_at
                              ? `${Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000)}s`
                              : `${Math.round((Date.now() - new Date(job.started_at).getTime()) / 1000)}s`
                            : 'Waiting...'
                          }
                        </div>
                        {(job.status === 'failed' || job.status === 'completed' || job.status === 'no_changes') && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await openRouterService.deleteRefinementJob(sessionId!, job.job_id, token!);
                                setRefinementJobs(prev => prev.filter(j => j.job_id !== job.job_id));
                              } catch (err) {
                                console.error('Failed to delete refinement job:', err);
                              }
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                            title="Delete job"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Token Usage */}
        {tokenUsage && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Token Usage:</span>
              <div className="flex gap-4">
                <span>Prompt: {tokenUsage.prompt_tokens.toLocaleString()}</span>
                <span>Completion: {tokenUsage.completion_tokens.toLocaleString()}</span>
                <span className="font-semibold">Total: {tokenUsage.total_tokens.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto space-y-6 mt-6 pr-2">
        {/* TTML Files List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold flex items-center justify-between">
            <span className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Metadata Revisions ({groupedTtmlFiles.length}{visibleTtmlFiles.length > groupedTtmlFiles.length ? ` • ${visibleTtmlFiles.length} visible versions` : ''})
            </span>
            {loadingCachedVersions && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
            )}
          </h3>
        </div>

        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/20">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <div>
                Visible cards show finalized revisions in each metadata lineage. Agent generation may create intermediate draft mutations that stay hidden here until a revision is finalized.
              </div>
            </div>
            {draftVersionCount > 0 ? (
              <button
                type="button"
                onClick={() => setShowDraftVersions(prev => !prev)}
                className="self-start text-[11px] px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 transition-colors"
              >
                {showDraftVersions ? `Hide ${draftVersionCount} draft mutation${draftVersionCount === 1 ? '' : 's'}` : `Show ${draftVersionCount} draft mutation${draftVersionCount === 1 ? '' : 's'}`}
              </button>
            ) : (
              <span className="text-[11px] text-gray-500 dark:text-gray-500">
                Draft intermediates are hidden unless the backend returns them.
              </span>
            )}
          </div>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {groupedTtmlFiles.map((file) => (
            <div
              key={file.id}
              className={`p-4 transition-colors cursor-pointer relative ${
                file.isSelected
                  ? 'border-l-4 border-[#FFB52B] bg-[#FFF8EA] dark:bg-[#2B3241]'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              onClick={(e) => {
                // Don't select if clicking on checkbox or buttons
                if ((e.target as HTMLElement).tagName === 'INPUT' ||
                    (e.target as HTMLElement).tagName === 'BUTTON' ||
                    (e.target as HTMLElement).closest('button')) {
                  return;
                }
                // Select this TTML file
                if (file.checksum && file.isValid !== false) {
                  console.log('[TTML-SELECT] Row clicked, selecting:', file.checksum?.substring(0, 8));
                  selectTtml(file.checksum);
                }
              }}
              onMouseEnter={() => {
                if (onTtmlPreview && file.isValid !== false) {
                  console.log('[TTML-HOVER] Row hover enter:', file.checksum?.substring(0, 8));
                  onTtmlPreview(file.checksum || file.id);
                }
              }}
              onMouseLeave={() => {
                if (onTtmlPreview) {
                  console.log('[TTML-HOVER] Row hover leave');
                  onTtmlPreview(null);
                }
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {file.name}
                    </h4>
                    {file.isDraft && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        Draft
                      </span>
                    )}
                    {file.isSelected && (
                      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium border border-[#FFB52B]/40 bg-[#FFF4D8] text-[#8B5B00] dark:border-[#FFB52B]/30 dark:bg-[#FFB52B]/[0.16] dark:text-[#FFE3A1]">
                        <Star className="h-3 w-3 mr-1" />
                        Selected
                      </span>
                    )}
                    {refiningFiles.has(file.checksum || '') && (
                      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium border border-[#445166] bg-[#232E3F] text-[#F4F4F4] animate-pulse">
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Refining...
                      </span>
                    )}
                    {file.isValid === true && (
                      file.validationWarnings && file.validationWarnings.length > 0 ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100 cursor-help"
                          title={`${file.validationWarnings.length} warnings: ${file.validationWarnings.slice(0, 3).map((w: any) => w.message).join('; ')}${file.validationWarnings.length > 3 ? '...' : ''}`}
                        >
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {file.validationWarnings.length} warnings
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium border border-[#E7B14C]/45 bg-[#FFF4D8] text-[#8B5B00] dark:border-[#8E6E33] dark:bg-[#332A17] dark:text-[#FFE3A1]">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Valid
                        </span>
                      )
                    )}
                    {file.isValid === false && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100 cursor-help"
                        title={file.validationErrors?.[0]?.message || file.validationErrors?.[0]?.type || 'Validation failed'}
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        {file.validationErrors?.[0]?.type?.replace(/_/g, ' ') || 'Invalid'}
                      </span>
                    )}
                    {file.isValid === undefined && !file.validationStats && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100">
                        <Clock className="h-3 w-3 mr-1 animate-pulse" />
                        Validating...
                      </span>
                    )}
                  </div>

                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {new Date(file.generatedAt).toLocaleString()}
                      </span>
                      <span>Model: {file.model}</span>
                      {/* Version chain selector - circular glyphs */}
                      {/* Version selector moved to bottom right of card */}
                    </div>
                    
                    {/* Token usage and/or cost display */}
                    {(file.tokenUsage?.total_tokens || (file.costUsd !== null && file.costUsd !== undefined)) && (
                      <div className="flex items-center gap-2">
                        <Activity className="h-3 w-3" />
                        {file.tokenUsage?.total_tokens && (
                          <span>Tokens: {file.tokenUsage.total_tokens.toLocaleString()}</span>
                        )}
                        {/* Show cumulative cost for version chains, otherwise single cost */}
                        {(() => {
                          // Calculate cumulative cost across version chain using pre-computed _chainVersions
                          const chainVersions = (file as any)._chainVersions || [file];
                          const cumulativeCost = chainVersions.reduce((sum: number, v: EnhancedTTMLFile) => sum + (v.costUsd || 0), 0);
                          const hasCumulativeCost = cumulativeCost > 0 && chainVersions.length > 1;

                          if (hasCumulativeCost) {
                            return (
                              <span className="text-[#B57700] dark:text-[#FFCF74]" title={`This version: $${(file.costUsd || 0).toFixed(2)} | Total across ${chainVersions.length} versions`}>
                                (Σ ${cumulativeCost.toFixed(2)})
                              </span>
                            );
                          } else if (cumulativeCost > 0) {
                            return (
                              <span className="text-[#B57700] dark:text-[#FFCF74]">
                                (${cumulativeCost.toFixed(2)})
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}

                    {/* Show generator - look at chain for version 2+ */}
                    {(() => {
                      // Try current file first, then look at chain for original generator
                      let generator = file.generatedBy;
                      if (!generator) {
                        const chainVersions = (file as any)._chainVersions || [];
                        const rootVersion = chainVersions.find((v: EnhancedTTMLFile) => v.version === 1 || !v.parentChecksum);
                        generator = rootVersion?.generatedBy;
                      }
                      return generator ? (
                        <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
                          <span>By: {generator}</span>
                        </div>
                      ) : null;
                    })()}

                    {file.validationStats && (
                      <div className="flex items-center gap-3 text-xs mt-1">
                        {/* Helper to render delta indicator */}
                        {(() => {
                          const deltas = calculateVersionDeltas[file.id];
                          const renderDelta = (delta: number) => {
                            if (!deltas?.hasParent || delta === 0) return null;
                            const isPositive = delta > 0;
                            return (
                              <span className={`ml-1 font-medium ${isPositive ? 'text-[#B57700] dark:text-[#FFCF74]' : 'text-red-600 dark:text-red-400'}`}>
                                {isPositive ? '+' : ''}{delta}
                              </span>
                            );
                          };

                          return (
                            <>
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                Topics: {file.validationStats.segmentsByType?.topic || 0}
                                {renderDelta(deltas?.topics || 0)}
                              </span>
                              <span className="inline-flex items-center rounded border border-[#445166] bg-[#232E3F] px-2 py-0.5 text-[#F4F4F4]">
                                Clips: {file.validationStats.segmentsByType?.clip || 0}
                                {renderDelta(deltas?.clips || 0)}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-700 dark:text-yellow-300">
                                Edits: {file.validationStats.segmentsByType?.edit || 0}
                                {renderDelta(deltas?.edits || 0)}
                              </span>
                              <span className="inline-flex items-center rounded border border-[#C7D0DB] bg-[#EEF1F3] px-2 py-0.5 text-[#3F4E62] dark:border-[#4F5C72] dark:bg-[#273244] dark:text-[#D5DCE5]">
                                Refs: {file.validationStats.segmentsByType?.reference || 0}
                                {renderDelta(deltas?.refs || 0)}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {file.isValid === false && file.validationErrors && file.validationErrors.length > 0 && (
                      <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                        <div className="font-medium text-red-800 dark:text-red-200 mb-1">
                          Validation Errors:
                        </div>
                        {file.validationErrors.slice(0, 3).map((error, idx) => (
                          <div key={idx} className="text-red-700 dark:text-red-300">
                            • {error.message}
                          </div>
                        ))}
                        {file.validationErrors.length > 3 && (
                          <div className="text-red-700 dark:text-red-300">
                            ... and {file.validationErrors.length - 3} more errors
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  </div>
                </div>

                <div className="flex items-center gap-1 ml-4">
                  <button
                    onClick={() => openViewTtmlModal(file)}
                    className="p-2 text-gray-600 hover:text-[#232E3F] dark:text-gray-400 dark:hover:text-[#FFCF74]"
                    title="View metadata revision"
                  >
                    <Eye className="h-4 w-4" />
                  </button>

                  {/* Show log button for refined versions or versions with revision notes */}
                  {((file.refinementLog && file.refinementLog.length > 0) || file.revisionNotes) && (
                    <button
                      onClick={() => setViewLogModal({ isOpen: true, file })}
                      className="p-2 text-gray-600 hover:text-amber-600 dark:text-gray-400 dark:hover:text-amber-400"
                      title={file.revisionNotes ? "View revision notes" : "View refinement log"}
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                  )}

                  <button
                    onClick={() => copyTtmlToClipboard(file)}
                    className="p-2 text-gray-600 hover:text-[#B57700] dark:text-gray-400 dark:hover:text-[#FFCF74]"
                    title="Copy TTML to clipboard"
                  >
                    <Copy className="h-4 w-4" />
                  </button>

                  {!sharePermissions && (
                    <button
                      onClick={() => openRefineModal(file)}
                      disabled={refiningFiles.has(file.checksum || '') || !file.checksum}
                      className={`p-2 disabled:opacity-50 ${
                        refiningFiles.has(file.checksum || '')
                          ? 'text-[#FFCF74]'
                          : 'text-gray-600 hover:text-[#232E3F] dark:text-gray-400 dark:hover:text-[#FFCF74]'
                      }`}
                      title={refiningFiles.has(file.checksum || '') ? 'Agent follow-up in progress...' : `Start targeted metadata follow-up in agent${enableWebAccess ? ' (with web access)' : ''}`}
                    >
                      {refiningFiles.has(file.checksum || '') ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <MessageSquare className="h-4 w-4" />
                      )}
                    </button>
                  )}

                  <button
                    onClick={() => downloadTtmlFile(file)}
                    className="p-2 text-gray-600 hover:text-[#B57700] dark:text-gray-400 dark:hover:text-[#FFCF74]"
                    title="Download TTML"
                  >
                    <Download className="h-4 w-4" />
                  </button>

                  {!sharePermissions && (
                    <button
                      onClick={() => deleteTtmlFile(file)}
                      className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      title="Delete metadata revision"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Version selector - positioned at bottom right, always shown */}
              {(() => {
                // Use pre-computed chain versions from groupedTtmlFiles
                const chainVersions = (file as any)._chainVersions || [file];
                const rootKey = (file as any)._chainRootKey || file.rootChecksum || file.checksum;

                // Check if any version in this chain is selected
                const anyVersionSelected = chainVersions.some((v: EnhancedTTMLFile) => v.isSelected);

                return (
                  <div className="absolute bottom-2 right-2 flex items-center gap-0.5" title="Version history - click to select">
                    {chainVersions.map((v: EnhancedTTMLFile, idx: number) => (
                      <React.Fragment key={v.checksum || idx}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('[TTML-ORB] Version orb clicked:', v.checksum?.substring(0, 8), 'version:', v.version);
                            // Select this version as the active TTML (if valid)
                            if (v.checksum && v.isValid !== false) {
                              selectTtml(v.checksum);
                            }
                            // Also update viewed version for this chain
                            if (v.checksum && rootKey) {
                              setViewedVersions(prev => ({
                                ...prev,
                                [rootKey]: v.checksum!
                              }));
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.stopPropagation();
                            console.log('[TTML-ORB] Version orb hover:', v.checksum?.substring(0, 8), 'version:', v.version);
                            // Preview on hover if valid
                            if (onTtmlPreview && v.isValid !== false && v.checksum) {
                              onTtmlPreview(v.checksum);
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.stopPropagation();
                            // Clear preview on mouse leave (let the row's onMouseLeave handle final clear)
                          }}
                          className={`w-4 h-4 rounded-full text-[10px] font-medium transition-all border ${
                            v.isSelected
                              ? 'border-[#FFB52B] bg-[#FFB52B] text-[#232E3F]'
                              : v.isDraft
                                ? 'bg-transparent text-amber-600 border-amber-400 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-600 dark:hover:bg-amber-900/20'
                                : v.isValid === false
                                  ? 'bg-transparent text-red-600 border-red-400 hover:bg-red-50 dark:text-red-400 dark:border-red-500 dark:hover:bg-red-900/30'
                                  : v.validationWarnings && v.validationWarnings.length > 0
                                    ? 'bg-transparent text-yellow-600 border-yellow-400 hover:bg-yellow-50 dark:text-yellow-400 dark:border-yellow-500 dark:hover:bg-yellow-900/30'
                                    : anyVersionSelected
                                      ? 'bg-transparent text-[#232E3F] border-[#445166] hover:bg-[#EEF1F3] dark:text-[#D5DCE5] dark:border-[#4F5C72] dark:hover:bg-[#273244]'
                                      : 'bg-transparent text-gray-400 border-gray-300 hover:bg-gray-50 dark:text-gray-500 dark:border-gray-600 dark:hover:bg-gray-800'
                          }`}
                          title={`Version ${v.version || 1}${v.isDraft ? ' (draft)' : v.isValid === false ? ' (invalid)' : v.validationWarnings && v.validationWarnings.length > 0 ? ` (${v.validationWarnings.length} warnings)` : v.isSelected ? ' (selected)' : ''} - click to select`}
                        >
                          {v.version || 1}
                        </button>
                        {/* Show no-op indicator if this version has no-op refinements */}
                        {v.noOpRefinements && v.noOpRefinements.length > 0 && (
                          <span
                            className="w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-700 cursor-help"
                            title={`${v.noOpRefinements.length} refinement(s) produced no changes - cost: $${v.noOpRefinements.reduce((sum, r) => sum + (r.costUsd || 0), 0).toFixed(2)}\nClick Log button to see details`}
                          >
                            !
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                );
              })()}
            </div>
          ))}

          {groupedTtmlFiles.length === 0 && !loadingCachedVersions && (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{draftVersionCount > 0 && !showDraftVersions ? 'No finalized metadata revisions are visible yet' : 'No metadata revisions generated yet'}</p>
              <p className="text-sm mt-2">
                {draftVersionCount > 0 && !showDraftVersions
                  ? 'Draft mutation revisions exist, but they are hidden by default. Use the toggle above to inspect them.'
                  : 'Click "Generate With Agent" to create your first metadata draft'}
              </p>
            </div>
          )}
        </div>
      </div>

        {/* Error and Notification Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
            >
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-3" />
                <p className="text-red-800 dark:text-red-200">{error}</p>
              </div>
            </motion.div>
          )}

          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="rounded-lg border border-[#E7B14C]/45 bg-[#FFF4D8] p-4 dark:border-[#8E6E33] dark:bg-[#332A17]"
            >
              <div className="flex items-center">
                <CheckCircle className="mr-3 h-5 w-5 text-[#B57700] dark:text-[#FFCF74]" />
                <p className="text-[#8B5B00] dark:text-[#FFE3A1]">{notification}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Enhanced View/Edit TTML Modal */}
      <AnimatePresence>
        {viewTtmlModal.isOpen && viewTtmlModal.file && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              if (hasEdits && !window.confirm('You have unsaved changes. Close anyway?')) return;
              setViewTtmlModal({ isOpen: false, file: null, content: '' });
              setHasEdits(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  {viewTtmlModal.file.name}
                  {viewTtmlModal.file.version && (
                    <span className="ml-2 rounded border border-[#445166] bg-[#232E3F] px-2 py-0.5 text-xs text-[#F4F4F4]">
                      v{viewTtmlModal.file.version}
                    </span>
                  )}
                  {hasEdits && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-700 dark:text-amber-300">
                      Edited
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  {hasEdits && (
                    <button
                      onClick={saveEditedTtml}
                      disabled={isSaving}
                      className="flex items-center gap-1 rounded bg-[#FFB52B] px-3 py-1.5 text-sm font-medium text-[#232E3F] transition-colors hover:bg-[#FFC54D] disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Save as New Version
                    </button>
                  )}
                  <button
                    onClick={() => copyToClipboard(editorTab === 'raw' ? editedContent : rebuildTtmlFromSegments())}
                    className="p-2 text-gray-600 hover:text-[#B57700] dark:text-gray-400 dark:hover:text-[#FFCF74]"
                    title="Copy to clipboard"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => downloadTtmlFile(viewTtmlModal.file!)}
                    className="p-2 text-gray-600 hover:text-[#B57700] dark:text-gray-400 dark:hover:text-[#FFCF74]"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (hasEdits && !window.confirm('You have unsaved changes. Close anyway?')) return;
                      setViewTtmlModal({ isOpen: false, file: null, content: '' });
                      setHasEdits(false);
                    }}
                    className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                {[
                  { id: 'raw' as const, label: 'Raw TTML' },
                  { id: 'topics' as const, label: `Topics (${parsedSegments.topics.length})` },
                  { id: 'clips' as const, label: `Clips (${parsedSegments.clips.filter(c => !c.clipTitle?.includes('PLACEHOLDER')).length})` },
                  { id: 'edits' as const, label: `Edits (${parsedSegments.edits.length})` },
                  { id: 'references' as const, label: `References (${parsedSegments.references.length})` }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setEditorTab(tab.id)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      editorTab === tab.id
                        ? 'border-[#FFB52B] text-[#8B5B00] dark:text-[#FFCF74]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Validation Error */}
              {validationError && (
                <div className="mx-4 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  <strong>Validation Error:</strong> {validationError}
                </div>
              )}

              {/* Content */}
              <div className="flex-1 overflow-auto p-4">
                {editorTab === 'raw' ? (
                  <textarea
                    value={editedContent}
                    onChange={(e) => {
                      setEditedContent(e.target.value);
                      setHasEdits(true);
                    }}
                    className="w-full h-full min-h-[400px] resize-none rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs font-mono focus:border-transparent focus:ring-2 focus:ring-[#FFB52B]/30 dark:border-gray-700 dark:bg-gray-900"
                    spellCheck={false}
                  />
                ) : editorTab === 'topics' ? (
                  /* Topics - structured form editing with contiguous time enforcement */
                  <div className="space-y-4">
                    {parsedSegments.topics.length === 0 ? (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                        No topic segments found in this TTML.
                      </div>
                    ) : (
                      parsedSegments.topics.map((segment, idx) => (
                        <div key={segment.id} className="rounded-lg border border-[#E7B14C]/45 bg-gray-50 p-4 dark:border-[#8E6E33] dark:bg-gray-900">
                          {/* Header with rank info and timestamps */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {/* Rank badge */}
                              <div className="flex items-center gap-1 rounded border border-[#E7B14C]/40 bg-[#FFF4D8] px-2 py-1 dark:border-[#8E6E33] dark:bg-[#332A17]">
                                <span className="text-sm font-bold text-[#8B5B00] dark:text-[#FFE3A1]">#{segment.topicRank || (idx + 1)}</span>
                                {segment.topicPlacement && (
                                  <span className="text-xs text-[#B57700] dark:text-[#FFCF74]">/{segment.topicPlacement.split('/')[1] || '?'}</span>
                                )}
                              </div>
                              {/* Score badge */}
                              {segment.topicScore && (
                                <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-gray-600 dark:text-gray-300">
                                  {segment.topicScore.split('(')[0].trim()}
                                </span>
                              )}
                              {/* Timestamps - contiguous enforcement */}
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={segment.begin}
                                  onChange={(e) => adjustContiguousTopicTimes(segment.id, 'begin', e.target.value)}
                                  className="w-28 text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
                                  placeholder="00:00:00.000"
                                  title={idx === 0 ? "First topic start time" : "Changing this also updates the previous topic's end time"}
                                />
                                <span className="text-gray-400">→</span>
                                <input
                                  type="text"
                                  value={segment.end}
                                  onChange={(e) => adjustContiguousTopicTimes(segment.id, 'end', e.target.value)}
                                  className="w-28 text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
                                  placeholder="00:00:00.000"
                                  title={idx === parsedSegments.topics.length - 1 ? "Last topic end time" : "Changing this also updates the next topic's start time"}
                                />
                              </div>
                            </div>
                            <button
                              onClick={() => deleteSegment('topics', segment.id)}
                              className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete topic"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {/* Structured fields */}
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Title</label>
                              <input
                                type="text"
                                value={segment.topicTitle || ''}
                                onChange={(e) => updateSegmentField('topics', segment.id, 'topicTitle', e.target.value)}
                                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFB52B]/30 dark:border-gray-600 dark:bg-gray-800"
                                placeholder="Topic title..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Justification</label>
                              <textarea
                                value={segment.topicJustification || ''}
                                onChange={(e) => updateSegmentField('topics', segment.id, 'topicJustification', e.target.value)}
                                className="w-full resize-y rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFB52B]/30 dark:border-gray-600 dark:bg-gray-800"
                                rows={2}
                                placeholder="Why this segment has this ranking..."
                              />
                            </div>
                            {segment.topicContinuity && (
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Continuity Note</label>
                                <input
                                  type="text"
                                  value={segment.topicContinuity || ''}
                                  onChange={(e) => updateSegmentField('topics', segment.id, 'topicContinuity', e.target.value)}
                                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFB52B]/30 dark:border-gray-600 dark:bg-gray-800"
                                  placeholder="Continuity with previous/next topics..."
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : editorTab === 'clips' ? (
                  /* Clips - structured form editing (hide PLACEHOLDER clips) */
                  <div className="space-y-4">
                    {parsedSegments.clips.filter(c => !c.clipTitle?.includes('PLACEHOLDER')).length === 0 ? (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                        No clip segments found in this TTML.
                      </div>
                    ) : (
                      parsedSegments.clips.filter(c => !c.clipTitle?.includes('PLACEHOLDER')).map((segment) => (
                        <div key={segment.id} className="rounded-lg border border-[#445166] bg-gray-50 p-4 dark:border-[#556176] dark:bg-gray-900">
                          {/* Header with timestamps and delete */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-[#232E3F] dark:text-[#FFCF74]">CLIP</span>
                              <input
                                type="text"
                                value={segment.begin}
                                onChange={(e) => updateSegmentTimestamp('clips', segment.id, 'begin', e.target.value)}
                                className="w-28 text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
                                placeholder="00:00:00.000"
                              />
                              <span className="text-gray-400">→</span>
                              <input
                                type="text"
                                value={segment.end}
                                onChange={(e) => updateSegmentTimestamp('clips', segment.id, 'end', e.target.value)}
                                className="w-28 text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
                                placeholder="00:00:00.000"
                              />
                            </div>
                            <button
                              onClick={() => deleteSegment('clips', segment.id)}
                              className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete clip"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {/* Structured fields */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Title</label>
                              <input
                                type="text"
                                value={segment.clipTitle || ''}
                                onChange={(e) => updateSegmentField('clips', segment.id, 'clipTitle', e.target.value)}
                                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#232E3F]/20 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#FFB52B]/25"
                                placeholder="Clip title..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Type</label>
                              <select
                                value={segment.clipType || ''}
                                onChange={(e) => updateSegmentField('clips', segment.id, 'clipType', e.target.value)}
                                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#232E3F]/20 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#FFB52B]/25"
                              >
                                <option value="">Select type...</option>
                                <option value="Viral Hook">Viral Hook</option>
                                <option value="Educational Nugget">Educational Nugget</option>
                                <option value="Humorous Aside">Humorous Aside</option>
                              </select>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
                              <textarea
                                value={segment.clipDescription || ''}
                                onChange={(e) => updateSegmentField('clips', segment.id, 'clipDescription', e.target.value)}
                                className="w-full resize-y rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#232E3F]/20 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#FFB52B]/25"
                                rows={2}
                                placeholder="Why this works as a clip..."
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Visual Suggestion</label>
                              <input
                                type="text"
                                value={segment.clipVisual || ''}
                                onChange={(e) => updateSegmentField('clips', segment.id, 'clipVisual', e.target.value)}
                                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#232E3F]/20 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#FFB52B]/25"
                                placeholder="Suggested visuals or b-roll..."
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : editorTab === 'edits' ? (
                  /* Edits - structured form editing */
                  <div className="space-y-4">
                    {parsedSegments.edits.length === 0 ? (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                        No edit segments found in this TTML.
                      </div>
                    ) : (
                      parsedSegments.edits.map((segment) => (
                        <div key={segment.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-red-200 dark:border-red-800">
                          {/* Header with timestamps and delete */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-red-600 dark:text-red-400 font-medium text-sm">EDIT</span>
                              <input
                                type="text"
                                value={segment.begin}
                                onChange={(e) => updateSegmentTimestamp('edits', segment.id, 'begin', e.target.value)}
                                className="w-28 text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
                                placeholder="00:00:00.000"
                              />
                              <span className="text-gray-400">→</span>
                              <input
                                type="text"
                                value={segment.end}
                                onChange={(e) => updateSegmentTimestamp('edits', segment.id, 'end', e.target.value)}
                                className="w-28 text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
                                placeholder="00:00:00.000"
                              />
                            </div>
                            <button
                              onClick={() => deleteSegment('edits', segment.id)}
                              className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete edit"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {/* Structured fields */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Title</label>
                              <input
                                type="text"
                                value={segment.editTitle || ''}
                                onChange={(e) => updateSegmentField('edits', segment.id, 'editTitle', e.target.value)}
                                className="w-full text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-red-500"
                                placeholder="Edit title..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Priority</label>
                              <select
                                value={segment.editPriority || ''}
                                onChange={(e) => updateSegmentField('edits', segment.id, 'editPriority', e.target.value)}
                                className="w-full text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-red-500"
                              >
                                <option value="">Select priority...</option>
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                              </select>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Suggestion</label>
                              <textarea
                                value={segment.editSuggestion || ''}
                                onChange={(e) => updateSegmentField('edits', segment.id, 'editSuggestion', e.target.value)}
                                className="w-full text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-red-500 resize-y"
                                rows={2}
                                placeholder="What to do (cut, trim, add context, etc.)..."
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Improvement</label>
                              <textarea
                                value={segment.editImprovement || ''}
                                onChange={(e) => updateSegmentField('edits', segment.id, 'editImprovement', e.target.value)}
                                className="w-full text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-red-500 resize-y"
                                rows={2}
                                placeholder="Why this edit helps..."
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  /* References - structured form editing */
                  <div className="space-y-4">
                    {parsedSegments.references.length === 0 ? (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                        No reference segments found in this TTML.
                      </div>
                    ) : (
                      parsedSegments.references.map((segment) => (
                        <div key={segment.id} className="rounded-lg border border-[#D7C3A0] bg-gray-50 p-4 dark:border-[#6A5A44] dark:bg-gray-900">
                          {/* Header with timestamps and delete */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-[#7A6346] dark:text-[#E4D7BD]">REF</span>
                              <input
                                type="text"
                                value={segment.begin}
                                onChange={(e) => updateSegmentTimestamp('references', segment.id, 'begin', e.target.value)}
                                className="w-28 text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
                                placeholder="00:00:00.000"
                              />
                              <span className="text-gray-400">→</span>
                              <input
                                type="text"
                                value={segment.end}
                                onChange={(e) => updateSegmentTimestamp('references', segment.id, 'end', e.target.value)}
                                className="w-28 text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
                                placeholder="00:00:00.000"
                              />
                            </div>
                            <button
                              onClick={() => deleteSegment('references', segment.id)}
                              className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete reference"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {/* Structured fields */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name</label>
                              <input
                                type="text"
                                value={segment.refName || ''}
                                onChange={(e) => updateSegmentField('references', segment.id, 'refName', e.target.value)}
                                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#D7C3A0]/45 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#6A5A44]/40"
                                placeholder="Reference name..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category</label>
                              <input
                                type="text"
                                value={segment.refCategory || ''}
                                onChange={(e) => updateSegmentField('references', segment.id, 'refCategory', e.target.value)}
                                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#D7C3A0]/45 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#6A5A44]/40"
                                placeholder="Person, Paper, Book, etc."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Link Text</label>
                              <input
                                type="text"
                                value={segment.refLinkText || ''}
                                onChange={(e) => updateSegmentField('references', segment.id, 'refLinkText', e.target.value)}
                                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#D7C3A0]/45 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#6A5A44]/40"
                                placeholder="arXiv Paper, Official Site, etc."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">URL</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="url"
                                  value={segment.refUrl || ''}
                                  onChange={(e) => updateSegmentField('references', segment.id, 'refUrl', e.target.value)}
                                  className="flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#D7C3A0]/45 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#6A5A44]/40"
                                  placeholder="https://..."
                                />
                                {segment.refUrl && segment.refUrl.startsWith('http') && (
                                  <a
                                    href={segment.refUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="rounded p-2 text-[#232E3F] transition-colors hover:bg-[#EEF1F3] hover:text-[#1B2433] dark:text-[#D5DCE5] dark:hover:bg-[#273244] dark:hover:text-[#FFCF74]"
                                    title="Open URL in new tab"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Context</label>
                              <textarea
                                value={segment.refContext || ''}
                                onChange={(e) => updateSegmentField('references', segment.id, 'refContext', e.target.value)}
                                className="w-full resize-y rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#D7C3A0]/45 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#6A5A44]/40"
                                rows={2}
                                placeholder="Why this reference is relevant..."
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Visual Suggestion</label>
                              <input
                                type="text"
                                value={segment.refVisual || ''}
                                onChange={(e) => updateSegmentField('references', segment.id, 'refVisual', e.target.value)}
                                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-[#D7C3A0]/45 dark:border-gray-600 dark:bg-gray-800 dark:focus:ring-[#6A5A44]/40"
                                placeholder="Photo, diagram, book cover, etc."
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Refinement Log Modal */}
      <AnimatePresence>
        {viewLogModal.isOpen && viewLogModal.file && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setViewLogModal({ isOpen: false, file: null })}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-amber-500" />
                  {viewLogModal.file.refinementLog && viewLogModal.file.refinementLog.length > 0
                    ? `Refinement Log - ${viewLogModal.file.name}`
                    : `Revision Notes - ${viewLogModal.file.name}`}
                  {viewLogModal.file.version && viewLogModal.file.version > 1 && (
                    <span className="ml-2 rounded border border-[#445166] bg-[#232E3F] px-2 py-0.5 text-xs text-[#F4F4F4]">
                      v{viewLogModal.file.version}
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => setViewLogModal({ isOpen: false, file: null })}
                  className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-3">
                {/* Revision Notes */}
                {viewLogModal.file.revisionNotes && (
                  <div className="mb-4 rounded-lg border border-[#E7B14C]/45 bg-gradient-to-r from-[#FFF8EA] to-[#F7E2AF] p-4 dark:border-[#8E6E33] dark:from-[#2C2A22] dark:to-[#3B3425]">
                    <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#8B5B00] dark:text-[#FFE3A1]">
                      <BookOpen className="h-4 w-4" />
                      {viewLogModal.file.editType === 'manual' ? 'Revision Notes' : 'Agent Revision Notes'}
                    </h4>
                    <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                      <ReactMarkdown>{viewLogModal.file.revisionNotes}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {viewLogModal.file.refinementLog?.map((logEntry: any, i: number) => {
                  // Determine the event type and styling
                  const getEventStyle = () => {
                    switch (logEntry.event) {
                      case 'generation_start':
                        return { icon: '▶️', label: 'Generation Started', color: 'text-[#3F4E62] dark:text-[#D5DCE5]', bg: 'bg-[#EEF1F3] dark:bg-[#273244]' };
                      case 'generation_complete':
                        return { icon: '✅', label: 'Generation Complete', color: 'text-[#B57700] dark:text-[#FFCF74]', bg: 'bg-[#FFF4D8] dark:bg-[#332A17]' };
                      case 'agent_init':
                        return { icon: '🤖', label: 'Agent Initialized', color: 'text-[#232E3F] dark:text-[#F4F4F4]', bg: 'bg-[#EEF1F3] dark:bg-[#232E3F]' };
                      case 'assistant_message':
                        return { icon: '💬', label: 'Assistant', color: 'text-[#232E3F] dark:text-[#D5DCE5]', bg: 'bg-[#EEF1F3] dark:bg-[#273244]' };
                      case 'tool_use':
                        return { icon: '🔧', label: `Tool: ${logEntry.tool}`, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20' };
                      case 'tool_result':
                        return { icon: logEntry.isError ? '❌' : '📋', label: 'Tool Result', color: logEntry.isError ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400', bg: logEntry.isError ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-800' };
                      case 'agent_result':
                        return { icon: '🏁', label: 'Agent Complete', color: 'text-[#B57700] dark:text-[#FFCF74]', bg: 'bg-[#FFF4D8] dark:bg-[#332A17]' };
                      case 'manual_refinement':
                        return { icon: '🔧', label: 'Manual Refinement', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20' };
                      case 'completed':
                        return { icon: '✅', label: 'Completed', color: 'text-[#B57700] dark:text-[#FFCF74]', bg: 'bg-[#FFF4D8] dark:bg-[#332A17]' };
                      default:
                        return { icon: '📝', label: logEntry.event, color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-800' };
                    }
                  };
                  const style = getEventStyle();

                  return (
                    <div key={i} className={`${style.bg} rounded-lg p-3 border-l-4 ${style.color.replace('text-', 'border-')}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${style.color}`}>
                          {style.icon} {style.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {logEntry.time ? new Date(logEntry.time).toLocaleTimeString() : ''}
                        </span>
                      </div>

                      {/* Event-specific content */}
                      {logEntry.event === 'assistant_message' && logEntry.text && (
                        <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 rounded p-2 mt-2 whitespace-pre-wrap max-h-48 overflow-y-auto">
                          {logEntry.text}
                        </div>
                      )}

                      {logEntry.event === 'tool_use' && logEntry.input && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900 rounded p-2 mt-2 font-mono overflow-x-auto">
                          <pre>{JSON.stringify(logEntry.input, null, 2)}</pre>
                        </div>
                      )}

                      {logEntry.event === 'tool_result' && logEntry.content && (
                        <div className={`text-xs ${logEntry.isError ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'} bg-white dark:bg-gray-900 rounded p-2 mt-2 font-mono overflow-x-auto max-h-32 overflow-y-auto`}>
                          {logEntry.content}
                        </div>
                      )}

                      {logEntry.event === 'agent_init' && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Model: <span className="font-medium">{logEntry.model}</span>
                          {logEntry.tools && <span className="ml-2">Tools: {logEntry.tools.length}</span>}
                        </div>
                      )}

                      {logEntry.event === 'agent_result' && (
                        <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                          {logEntry.durationMs && (
                            <div className="bg-white dark:bg-gray-900 p-2 rounded">
                              Duration: <span className="font-medium">{(logEntry.durationMs / 1000).toFixed(1)}s</span>
                            </div>
                          )}
                          {logEntry.numTurns && (
                            <div className="bg-white dark:bg-gray-900 p-2 rounded">
                              Turns: <span className="font-medium">{logEntry.numTurns}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {logEntry.event === 'generation_complete' && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
                          {logEntry.isValid !== undefined && (
                            <div className={`p-2 rounded ${logEntry.isValid ? 'bg-[#FFF4D8] dark:bg-[#332A17]' : 'bg-red-100 dark:bg-red-900/30'}`}>
                              Valid: <span className={`font-medium ${logEntry.isValid ? 'text-[#B57700]' : 'text-red-600'}`}>{logEntry.isValid ? 'Yes' : 'No'}</span>
                            </div>
                          )}
                          {logEntry.errors !== undefined && (
                            <div className="bg-white dark:bg-gray-900 p-2 rounded">
                              Errors: <span className="font-medium">{logEntry.errors}</span>
                            </div>
                          )}
                          {logEntry.warnings !== undefined && (
                            <div className="bg-white dark:bg-gray-900 p-2 rounded">
                              Warnings: <span className="font-medium">{logEntry.warnings}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Stats for generation_complete */}
                      {logEntry.stats && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Topics: {logEntry.stats.segmentsByType?.topic || 0} |
                          Clips: {logEntry.stats.segmentsByType?.clip || 0} |
                          Edits: {logEntry.stats.segmentsByType?.edit || 0} |
                          Refs: {logEntry.stats.segmentsByType?.reference || 0}
                        </div>
                      )}
                    </div>
                  );
                })}
                {(!viewLogModal.file.refinementLog || viewLogModal.file.refinementLog.length === 0) &&
                 (!viewLogModal.file.noOpRefinements || viewLogModal.file.noOpRefinements.length === 0) && (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No activity log available for this version.
                  </div>
                )}

                {/* Show no-op refinements if any */}
                {viewLogModal.file.noOpRefinements && viewLogModal.file.noOpRefinements.length > 0 && (
                  <div className="mt-4 border-t border-amber-200 dark:border-amber-800 pt-4">
                    <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2 flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      No-Change Refinements ({viewLogModal.file.noOpRefinements.length})
                    </h4>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
                      These refinement attempts produced no changes to the metadata revision content.
                    </p>
                    <div className="space-y-2">
                      {viewLogModal.file.noOpRefinements.map((noOp: any, i: number) => (
                        <div key={i} className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                              Iteration {noOp.iteration}
                            </span>
                            <span className="text-xs text-amber-600 dark:text-amber-400">
                              ${noOp.costUsd?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                          <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                            Model: {noOp.model?.split('-').slice(-2).join('-') || 'unknown'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {noOp.timestamp ? new Date(noOp.timestamp).toLocaleString() : ''}
                          </div>
                          {noOp.message && (
                            <div className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                              {noOp.message}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                      Total cost for no-op refinements: ${viewLogModal.file.noOpRefinements.reduce((sum: number, r: any) => sum + (r.costUsd || 0), 0).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Refine Modal */}
      <AnimatePresence>
        {refineModal.isOpen && refineModal.file && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setRefineModal(prev => ({ ...prev, isOpen: false }))}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <MessageSquare className="mr-2 h-5 w-5 text-[#232E3F] dark:text-[#FFCF74]" />
                  Metadata Follow-Up
                </h3>
                <button
                  onClick={() => setRefineModal(prev => ({ ...prev, isOpen: false }))}
                  className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Selected Revision: <span className="font-medium text-gray-900 dark:text-white">{refineModal.file.name}</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-md p-3">
                  Use this for targeted fixes, edits, or timeline-cut analysis after the main validator-gated generation pass. First-pass metadata creation now starts from the Generate With Agent button.
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Agent Model (Anthropic)
                  </label>
                  <select
                    value={selectedRefinementModel}
                    onChange={(e) => setSelectedRefinementModel(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    {refinementModels.map(model => (
                      <option key={model.id} value={model.id}>{model.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Additional Instructions (optional)
                  </label>
                  <textarea
                    value={refineModal.instructions}
                    onChange={(e) => {
                      const newInstructions = e.target.value;
                      setRefineModal(prev => ({
                        ...prev,
                        instructions: newInstructions,
                        // Auto-enable blank slate when user enters instructions
                        blankSlate: newInstructions.trim().length > 0 ? true : prev.blankSlate
                      }));
                    }}
                    placeholder="e.g., Focus on improving topic boundaries, add more detailed clip descriptions, fix the intro timestamp..."
                    className="min-h-[100px] w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Number of Refinement Iterations
                    </label>
                    <div className="flex items-center gap-3">
                      {[1, 2, 3, 5].map(count => (
                        <button
                          key={count}
                          onClick={() => setRefineModal(prev => ({ ...prev, iterationCount: count }))}
                          className={`px-4 py-2 rounded-md font-medium transition-colors ${
                            refineModal.iterationCount === count
                              ? 'bg-[#232E3F] text-[#F4F4F4]'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {count}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Each iteration will try to improve the TTML further based on validation and quality criteria.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Issues to Fix per Pass
                    </label>
                    <div className="flex items-center gap-3">
                      {[{ value: 0, label: 'All' }, { value: 2, label: '2' }, { value: 5, label: '5' }, { value: 10, label: '10' }].map(option => (
                        <button
                          key={option.value}
                          onClick={() => setRefineModal(prev => ({ ...prev, maxIssuesToFix: option.value }))}
                          className={`px-4 py-2 rounded-md font-medium transition-colors ${
                            refineModal.maxIssuesToFix === option.value
                              ? 'bg-[#232E3F] text-[#F4F4F4]'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {refineModal.maxIssuesToFix === 0
                        ? 'Fix all validation errors and warnings in one comprehensive pass.'
                        : `Fix up to ${refineModal.maxIssuesToFix} issues per iteration. Use lower values for more controlled, incremental fixes.`}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <input
                      type="checkbox"
                      id="refine-web-access"
                      checked={enableWebAccess}
                      onChange={(e) => setEnableWebAccess(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#FFB52B] focus:ring-[#FFB52B]"
                    />
                    <label htmlFor="refine-web-access" className="text-sm text-gray-700 dark:text-gray-300">
                      Enable web access for reference URL lookup
                    </label>
                  </div>

                  <div className={`flex items-start gap-2 p-3 rounded-lg border ${
                    refineModal.blankSlate
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                      : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }`}>
                    <input
                      type="checkbox"
                      id="refine-blank-slate"
                      checked={refineModal.blankSlate}
                      onChange={(e) => setRefineModal(prev => ({ ...prev, blankSlate: e.target.checked }))}
                      className="w-4 h-4 mt-0.5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />
                    <div>
                      <label htmlFor="refine-blank-slate" className={`text-sm font-medium ${
                        refineModal.blankSlate
                          ? 'text-amber-800 dark:text-amber-200'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        Blank Slate Mode {refineModal.instructions.trim() && refineModal.blankSlate && <span className="text-xs font-normal">(auto-enabled)</span>}
                      </label>
                      <p className={`text-xs mt-0.5 ${
                        refineModal.blankSlate
                          ? 'text-amber-700 dark:text-amber-300'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {refineModal.blankSlate ? (
                          <>
                            <strong>Recommended when using custom instructions.</strong> The agent will follow only your instructions above instead of the generic refinement prompt.
                            Without blank slate, the TTML may be returned unchanged if there are no validation errors.
                          </>
                        ) : (
                          'Skip the standard refinement prompt. Only your instructions will guide the refinement.'
                        )}
                      </p>
                    </div>
                  </div>

                  {refineModal.blankSlate && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <label className="block text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                        Topic Density (per hour)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="5"
                          max="30"
                          value={refineModal.topicDensity}
                          onChange={(e) => setRefineModal(prev => ({ ...prev, topicDensity: parseInt(e.target.value) }))}
                          className="flex-1 h-2 bg-amber-200 dark:bg-amber-700 rounded-lg appearance-none cursor-pointer accent-amber-600"
                        />
                        <span className="text-sm font-medium text-amber-800 dark:text-amber-200 w-8 text-center">
                          {refineModal.topicDensity}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-amber-600 dark:text-amber-400 mt-1">
                        <span>Sparse (5)</span>
                        <span>Medium (12)</span>
                        <span>Dense (18)</span>
                        <span>Very Dense (30)</span>
                      </div>
                      <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                        {refineModal.topicDensity <= 8 ? 'Fewer, broader topic segments for high-level overviews.' :
                         refineModal.topicDensity <= 14 ? 'Balanced topic segmentation for most content.' :
                         refineModal.topicDensity <= 22 ? 'More detailed topic segments (default).' :
                         'Maximum granularity with many specific topic segments.'}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setRefineModal(prev => ({ ...prev, isOpen: false }))}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitRefinement}
                    className="flex items-center rounded-md bg-[#232E3F] px-4 py-2 text-[#F4F4F4] transition-colors hover:bg-[#2F3B4E]"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Start Follow-Up Session
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Generate TTML Modal */}
      <AnimatePresence>
        {generateModal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setGenerateModal(prev => ({ ...prev, isOpen: false }))}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Zap className="mr-2 h-5 w-5 text-[#B57700] dark:text-[#FFCF74]" />
                  Generate Metadata With Agent
                </h3>
                <button
                  onClick={() => setGenerateModal(prev => ({ ...prev, isOpen: false }))}
                  className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Agent Model
                  </label>
                  <select
                    value={selectedRefinementModel}
                    onChange={(e) => setSelectedRefinementModel(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    {refinementModels.map(model => (
                      <option key={model.id} value={model.id}>{model.name}</option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Uses the agent system directly and only finishes once the generated metadata revision validates cleanly.
                  </p>
                </div>

                {/* Topic Density Slider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Topic Density (per hour)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="5"
                      max="30"
                      value={generateModal.topicDensity}
                      onChange={(e) => setGenerateModal(prev => ({ ...prev, topicDensity: parseInt(e.target.value) }))}
                      className="flex-1 h-2 cursor-pointer appearance-none rounded-lg bg-gray-200 accent-[#FFB52B] dark:bg-gray-600"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-8 text-center">
                      {generateModal.topicDensity}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>Sparse (5)</span>
                    <span>Medium (12)</span>
                    <span>Dense (18)</span>
                    <span>Very Dense (30)</span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {generateModal.topicDensity <= 8 ? 'Fewer, broader topic segments for high-level overviews.' :
                     generateModal.topicDensity <= 14 ? 'Balanced topic segmentation for most content.' :
                     generateModal.topicDensity <= 22 ? 'More detailed topic segments (default).' :
                     'Maximum granularity with many specific topic segments.'}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="generate-web-search"
                    checked={generateModal.enableWebSearch}
                    onChange={(e) => setGenerateModal(prev => ({ ...prev, enableWebSearch: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-[#FFB52B] focus:ring-[#FFB52B]"
                  />
                  <label htmlFor="generate-web-search" className="text-sm text-gray-700 dark:text-gray-300">
                    Verify references online during generation
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 ml-6">
                  Enables the agent&apos;s `web_search` and `web_fetch` tools for reference sanity checking before final validation.
                </p>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="generate-include-edits"
                    checked={generateModal.includeEdits}
                    onChange={(e) => setGenerateModal(prev => ({ ...prev, includeEdits: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-[#FFB52B] focus:ring-[#FFB52B]"
                  />
                  <label htmlFor="generate-include-edits" className="text-sm text-gray-700 dark:text-gray-300">
                    Create edit suggestions track
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 ml-6">
                  {isPublishedOrYoutubeLinkedSession
                    ? 'Disabled by default for published or YouTube-linked sessions. Keep public metadata to topics, clips, and references unless a human explicitly needs an edits pass.'
                    : 'Turn this off for distribution-ready metadata where edit recommendations are no longer useful.'}
                </p>

                {/* Additional Instructions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Additional Instructions (Optional)
                  </label>
                  <textarea
                    value={generateModal.additionalInstructions}
                    onChange={(e) => setGenerateModal(prev => ({ ...prev, additionalInstructions: e.target.value }))}
                    placeholder="Add context about the content, guest names, specific focus areas, or instructions like 'reuse the verified references from episode XYZ but keep current timings'..."
                    className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/30 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    rows={3}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    These instructions will be added to the generation prompt. The current agent is still write-scoped to this session, so cross-session reuse works best through explicit read-only lookup tools rather than direct mutation.
                  </p>
                </div>

                <div className="rounded-lg border border-[#E7B14C]/45 bg-[#FFF8EA] p-3 text-xs text-[#8B5B00] dark:border-[#8E6E33] dark:bg-[#332A17] dark:text-[#FFE3A1]">
                  The agent will create a blank metadata revision, build it incrementally with structured mutation tools, validate after each major stage, and only finish once the final revision passes validation.
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setGenerateModal(prev => ({ ...prev, isOpen: false }))}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setGenerateModal(prev => ({ ...prev, isOpen: false }));
                      // Start generation with all options
                      await generateTTML({
                        topicDensity: generateModal.topicDensity,
                        enableWebSearch: generateModal.enableWebSearch,
                        includeEdits: generateModal.includeEdits,
                        additionalInstructions: generateModal.additionalInstructions
                      });
                    }}
                    className="flex items-center rounded-md bg-[#FFB52B] px-4 py-2 text-[#232E3F] transition-colors hover:bg-[#FFC54D]"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Generate With Agent
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <McpSetupModal
        isDarkMode={typeof document !== 'undefined' && document.documentElement.classList.contains('dark')}
        isOpen={showMcpModal}
        onClose={() => setShowMcpModal(false)}
        title="MLST ReScript MCP"
      />

    </div>
  );
};

export default TTMLRankingEnhanced;
